import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { sortByName, sortByTagId } from '../lib/sortByName';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api, { cardsAPI, employeesAPI, locationsAPI, organizationsAPI, uploadAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';
import {
    CreditCard,
    Search,
    Plus,
    User,
    Wifi,
    Filter,
    MoreVertical,
    UserPlus,
    UserMinus,
    Trash2,
    Upload,
    FileSpreadsheet,
    Globe,
    CheckSquare,
    Lock,
    Building2,
    MapPin,
    Edit3,
    RefreshCw,
    HelpCircle,
    Smartphone,
    Image,
    ExternalLink,
    X,
    Copy,
    Images,
    Loader2,
    Clock,
    Calendar,
    Shield,
    Link2,
    QrCode,
    Layers,
    Download
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { serializeCardSettings } from '../utils/serializeCardSettings';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription,
} from '../components/ui/dialog';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from '../components/ui/tabs';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '../components/ui/tooltip';
import { 
    EmptyState, 
    InfoBanner,
    HelpTooltip 
} from '../components/UsabilityComponents';
import { CardSettingsModal } from '../components/cards/CardSettingsModal';
import { BulkApplyModal } from '../components/cards/BulkApplyModal';
import { ModeSwitchDialog } from '../components/cards/ModeSwitchDialog';
import { QrCodeModal } from '../components/cards/QrCodeModal';
import BulkCreateCardsDialog from '../components/cards/BulkCreateCardsDialog';
import { QRCodeSVG } from 'qrcode.react';

const PLATFORMS = [
    { value: 'google_reviews', label: 'Google Reviews' },
    { value: 'facebook', label: 'Facebook Reviews' },
    { value: 'instagram', label: 'Instagram' },
    { value: 'trustpilot', label: 'Trustpilot' },
    { value: 'yelp', label: 'Yelp' },
    { value: 'tripadvisor', label: 'Tripadvisor' }
];

// Offered for every vertical, on top of the six above.
const EXTRA_CARD_PLATFORMS = [
    { value: 'tiktok', label: 'TikTok' }
];

// MEDICAL ONLY — the clinic's own link slots (set in the clinic's routing, not
// re-pointed from here) plus its review site.
const MEDICAL_CARD_PLATFORMS = [
    { value: 'ratemds', label: 'RateMDs' }
];
const MEDICAL_CARD_LINK_SLOTS = [
    { value: 'custom_1', label: 'Booking link' },
    { value: 'custom_2', label: 'Services link' }
];

// Friendly label used by the "Currently routing to" indicator (Timing System).
function formatPlatformLabel(value) {
    if (!value) return '';
    const m = {
        google_reviews: 'Google',
        facebook: 'Facebook',
        instagram: 'Instagram',
        trustpilot: 'Trustpilot',
        yelp: 'Yelp',
        tripadvisor: 'TripAdvisor',
    };
    return m[value] || value;
}

// Build a compact schedule summary string, e.g.
//   "Mon–Sun 09:00–12:00 Google · 12:00–17:00 Facebook · 17:00–23:59 Instagram"
// Used for the read-only tooltip on the "Currently routing to" indicator.
function formatScheduleSummary(schedule) {
    if (!Array.isArray(schedule) || schedule.length === 0) return '';
    const DAY_ORDER = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
    const DAY_SHORT = { mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun' };
    const summarizeDays = (days) => {
        const set = new Set((days || []).map(d => String(d).toLowerCase().slice(0, 3)));
        if (set.size === 7) return 'Every day';
        const ordered = DAY_ORDER.filter(d => set.has(d));
        return ordered.map(d => DAY_SHORT[d]).join(', ');
    };
    return schedule
        .map((b) => `${summarizeDays(b.days)} ${b.start_time || '?'}–${b.end_time || '?'} → ${formatPlatformLabel(b.platform)}`)
        .join('\n');
}



const Cards = () => {
    const { t } = useTranslation();
    const [cards, setCards] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [locations, setLocations] = useState([]);
    const [organizations, setOrganizations] = useState([]);
    const [organization, setOrganization] = useState(null);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const [filterLocation, setFilterLocation] = useState('all');
    const [filterOrganization, setFilterOrganization] = useState('all');
    
    // Selection for bulk operations
    const [selectedCards, setSelectedCards] = useState([]);
    const [isBulkPlatformModalOpen, setIsBulkPlatformModalOpen] = useState(false);
    const [isBulkOrgModalOpen, setIsBulkOrgModalOpen] = useState(false);
    const [bulkPlatform, setBulkPlatform] = useState('');
    const [bulkOrganization, setBulkOrganization] = useState('');
    const [bulkLocation, setBulkLocation] = useState('');
    
    // Modals
    const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    // v1.1 — Bulk Sequential Card Creation dialog. Lives next to the
    // existing single-create modal in the toolbar; reuses POST /cards/bulk.
    const [isBulkCreateOpen, setIsBulkCreateOpen] = useState(false);
    const [isEditOrgModalOpen, setIsEditOrgModalOpen] = useState(false);
    const [isEditNameModalOpen, setIsEditNameModalOpen] = useState(false);
    const [isEditLocationModalOpen, setIsEditLocationModalOpen] = useState(false);
    const [isLandingSettingsModalOpen, setIsLandingSettingsModalOpen] = useState(false);
    const [selectedCard, setSelectedCard] = useState(null);
    // Dirty-state tracking for card settings (P0 Save-required rule)
    const [cardSettingsSnapshot, setCardSettingsSnapshot] = useState(null);
    const [isSavingCardSettings, setIsSavingCardSettings] = useState(false);
    const [cardSettingsExitGuard, setCardSettingsExitGuard] = useState(false);
    const pendingCloseRef = useRef(null);
    const [selectedEmployee, setSelectedEmployee] = useState('');
    const [editOrganization, setEditOrganization] = useState('');
    const [editLocation, setEditLocation] = useState('');
    const [editCardName, setEditCardName] = useState('');
    
    // Landing page settings
    const [landingPageEnabled, setLandingPageEnabled] = useState(false);
    const [landingHeadline, setLandingHeadline] = useState('');
    const [landingDescription, setLandingDescription] = useState('');
    const [landingLogoUrl, setLandingLogoUrl] = useState('');
    const [landingLogoSize, setLandingLogoSize] = useState('medium');
    const [landingAutoRedirect, setLandingAutoRedirect] = useState(true);
    const [landingRedirectDelay, setLandingRedirectDelay] = useState(5);
    const [countdownClockEnabled, setCountdownClockEnabled] = useState(true);
    const [countdownClockColor, setCountdownClockColor] = useState('blue');
    const [landingShowText, setLandingShowText] = useState(true);
    const [showYulontapBranding, setShowYulontapBranding] = useState(true);
    const [uploadingLogo, setUploadingLogo] = useState(false);
    const [showLandingPreview, setShowLandingPreview] = useState(false);
    const logoInputRef = useRef(null);
    
    // Multiple Ads Feature
    const [multipleAdsEnabled, setMultipleAdsEnabled] = useState(false);
    const [adImages, setAdImages] = useState([]);
    const [sameTextForAll, setSameTextForAll] = useState(true);
    const [adTexts, setAdTexts] = useState([]);
    const [uploadingAdImage, setUploadingAdImage] = useState(false);
    const adImageInputRef = useRef(null);
    
    // Bulk Apply State
    const [showBulkApplyModal, setShowBulkApplyModal] = useState(false);
    const [bulkApplyOrg, setBulkApplyOrg] = useState('');
    const [bulkApplyLocation, setBulkApplyLocation] = useState('');
    const [bulkApplying, setBulkApplying] = useState(false);
    
    // Schedule Feature State
    const [scheduleEnabled, setScheduleEnabled] = useState(false);
    const [scheduleTimezone, setScheduleTimezone] = useState('America/New_York');
    const [scheduleEntries, setScheduleEntries] = useState([]);
    
    // Interaction Mode
    const [interactionMode, setInteractionMode] = useState('standard');
    const [showModeSwitchConfirm, setShowModeSwitchConfirm] = useState(false);
    const [cardUrlInfo, setCardUrlInfo] = useState(null);
    const [qrModalCardId, setQrModalCardId] = useState(null);
    const [qrModalIsAutomotive, setQrModalIsAutomotive] = useState(false);
    const [pendingMode, setPendingMode] = useState(null);
    
    // Review Shield specific settings
    const [reviewShieldLogoUrl, setReviewShieldLogoUrl] = useState('');
    const [reviewShieldLogoSize, setReviewShieldLogoSize] = useState('medium');
    // v3.5.18 — Per-card wallpaper for Review Shield. Empty string => use org-level
    // brand from voucher_settings (resolved server-side); BrandingShell falls back
    // to the dark gradient when neither is set.
    const [reviewShieldBackgroundImageUrl, setReviewShieldBackgroundImageUrl] = useState('');
    // v3.5.19 — Per-card override for the welcome card surface (frosted/clear/solid).
    // Empty string => inherit from voucher_settings.card_style (org default).
    const [cardStyle, setCardStyle] = useState('');
    const [languageMode, setLanguageMode] = useState('bilingual');

    // v3.3.1 — per-card override for enabled_languages. null = inherit from org.
    const [enabledLanguages, setEnabledLanguages] = useState(null);
    // Per-card override for the Gift Generator's Lead Generation step.
    // null = inherit the org's Voucher Settings; {enabled, url} = override.
    const [leadGenerationOverride, setLeadGenerationOverride] = useState(null);
    // Which named Gift Generator setup this card runs. null = org default.
    const [giftGeneratorSetupId, setGiftGeneratorSetupId] = useState(null);
    const [reviewShieldQuestions, setReviewShieldQuestions] = useState([]);
    // Public Review Threshold — minimum star rating that prompts a public
    // Google review (default 4 preserves legacy behaviour).
    const [publicReviewThreshold, setPublicReviewThreshold] = useState(4);
    const [privateReviewEnabled, setPrivateReviewEnabled] = useState(false);
    // v4.38.0 — Review Shield experience type. 'dine_in' default preserves
    // existing behaviour; 'pickup' / 'delivery' branch the customer flow.
    const [experienceType, setExperienceType] = useState('dine_in');
    // Org that owns the currently displayed Experience Type (org-wide setting).
    const [rsExperienceOrgId, setRsExperienceOrgId] = useState(null);
    const [savingExperienceType, setSavingExperienceType] = useState(false);
    // Guest Experience Version (V2 visual enhancement) — presentation-only.
    // 'v1' Classic (default, existing experience) | 'v2' Enhanced visuals.
    const [guestExperienceVersion, setGuestExperienceVersion] = useState('v1');
    // Timing System — OFF by default; only the redirect destination can change by time.
    const [timingEnabled, setTimingEnabled] = useState(false);
    const [timingSchedule, setTimingSchedule] = useState([]);
    const [notificationEmails, setNotificationEmails] = useState([]);
    const [notificationPhones, setNotificationPhones] = useState([]);
    const [directResponseEnabled, setDirectResponseEnabled] = useState(false);
    const [requireEmployeePin, setRequireEmployeePin] = useState(true);
    const [reviewCounterEnabled, setReviewCounterEnabled] = useState(false);
    // P_TAP_PROTECTION_1.0 — canonical Tap Protection state
    const [tapProtectionEnabled, setTapProtectionEnabled] = useState(true);
    const [testMode, setTestMode] = useState(false);
    const [duplicateProtectionEnabled, setDuplicateProtectionEnabled] = useState(true);
    const [duplicateWindowSeconds, setDuplicateWindowSeconds] = useState(30);
    const [dwellProtectionEnabled, setDwellProtectionEnabled] = useState(true);
    const [dwellThresholdSeconds, setDwellThresholdSeconds] = useState(10);
    const [giftCardEnabled, setGiftCardEnabled] = useState(false);
    // Loyal Review — merchant configuration state. When enabled AND a
    // reward voucher id is set, positive raters see the gift-vs-review
    // choice screen in the customer flow (ReviewShield.jsx).
    const [loyalReviewEnabled, setLoyalReviewEnabled] = useState(false);
    const [loyalReviewVoucherId, setLoyalReviewVoucherId] = useState('');
    const [loyalReviewAutoFlow, setLoyalReviewAutoFlow] = useState(false);
    const [giftGeneratorEnabled, setGiftGeneratorEnabled] = useState(false);
    const reviewShieldLogoInputRef = useRef(null);
    const reviewShieldWallpaperInputRef = useRef(null);
    const globalReviewLogoInputRef = useRef(null);
    const globalReviewWallpaperInputRef = useRef(null);

    
    // Global Review settings
    const [globalReviewEnabled, setGlobalReviewEnabled] = useState(false);
    const [useReviewShieldWithGlobalReview, setUseReviewShieldWithGlobalReview] = useState(false);
    const [globalReviewDestinations, setGlobalReviewDestinations] = useState({});
    const [globalReviewLogoUrl, setGlobalReviewLogoUrl] = useState('');
    const [globalReviewLogoSize, setGlobalReviewLogoSize] = useState('medium');
    // v3.5.18 — Per-card wallpaper for Global Review. Cascades through
    // voucher_settings.background_image_url (org default) → none.
    const [globalReviewBackgroundImageUrl, setGlobalReviewBackgroundImageUrl] = useState('');
    const [globalReviewMessageEn, setGlobalReviewMessageEn] = useState('');
    const [globalReviewMessageFr, setGlobalReviewMessageFr] = useState('');
    // Hotel Ops (hotel_staff) per-card branding — logo + optional wallpaper override.
    const [hotelOpsLogoUrl, setHotelOpsLogoUrl] = useState('');
    const [hotelOpsLogoSize, setHotelOpsLogoSize] = useState('medium');
    const [hotelOpsBackgroundImageUrl, setHotelOpsBackgroundImageUrl] = useState('');
    const hotelOpsLogoInputRef = useRef(null);
    const hotelOpsWallpaperInputRef = useRef(null);
    // Guest Services Entry Context (per-card; guest_assistant mode only).
    const [guestServicesContext, setGuestServicesContext] = useState('standard');
    const [poolDrinksEnabled, setPoolDrinksEnabled] = useState(true);
    const [poolFoodEnabled, setPoolFoodEnabled] = useState(true);
    const [poolTowelsEnabled, setPoolTowelsEnabled] = useState(true);
    const [poolOtherEnabled, setPoolOtherEnabled] = useState(true);
    const [poolDrinkMenuImageUrl, setPoolDrinkMenuImageUrl] = useState('');
    const [poolFoodMenuImageUrl, setPoolFoodMenuImageUrl] = useState('');
    const poolDrinkMenuInputRef = useRef(null);
    const poolFoodMenuInputRef = useRef(null);

    
    // Landing Page Video / Media settings
    const [landingVideoEnabled, setLandingVideoEnabled] = useState(false);
    const [mediaType, setMediaType] = useState(null);
    const [mediaVideoUrl, setMediaVideoUrl] = useState('');
    const [mediaVideoId, setMediaVideoId] = useState('');
    const [mediaVideoPosterUrl, setMediaVideoPosterUrl] = useState('');
    const [mediaVideoFallbackImage, setMediaVideoFallbackImage] = useState('');
    const [mediaAutoplay, setMediaAutoplay] = useState(true);
    const [mediaLoop, setMediaLoop] = useState(false);
    const [mediaMuted, setMediaMuted] = useState(true);
    const [mediaSkipEnabled, setMediaSkipEnabled] = useState(true);
    const [mediaSkipDelay, setMediaSkipDelay] = useState(5);
    const [uploadingVideo, setUploadingVideo] = useState(false);
    const [videoUploadProgress, setVideoUploadProgress] = useState(0);
    const videoInputRef = useRef(null);
    
    // Common timezones for dropdown
    const timezones = [
        { value: 'America/New_York', label: 'Eastern Time (ET)' },
        { value: 'America/Chicago', label: 'Central Time (CT)' },
        { value: 'America/Denver', label: 'Mountain Time (MT)' },
        { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
        { value: 'America/Phoenix', label: 'Arizona (MST)' },
        { value: 'America/Anchorage', label: 'Alaska (AKT)' },
        { value: 'Pacific/Honolulu', label: 'Hawaii (HST)' },
        { value: 'America/Toronto', label: 'Toronto (ET)' },
        { value: 'America/Vancouver', label: 'Vancouver (PT)' },
        { value: 'Europe/London', label: 'London (GMT/BST)' },
        { value: 'Europe/Paris', label: 'Paris (CET)' },
        { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
        { value: 'Australia/Sydney', label: 'Sydney (AEST)' },
    ];
    
    const daysOfWeek = [
        { value: 'monday', label: 'Mon' },
        { value: 'tuesday', label: 'Tue' },
        { value: 'wednesday', label: 'Wed' },
        { value: 'thursday', label: 'Thu' },
        { value: 'friday', label: 'Fri' },
        { value: 'saturday', label: 'Sat' },
        { value: 'sunday', label: 'Sun' },
    ];
    
    // Create card form
    const [newCardIds, setNewCardIds] = useState('');
    const [newCardOrganization, setNewCardOrganization] = useState('');
    const [newCardLocation, setNewCardLocation] = useState('');
    const [newCardPlatform, setNewCardPlatform] = useState('');
    const [createTab, setCreateTab] = useState('manual');
    const fileInputRef = useRef(null);
    
    const { user, canAssignCards, canChangePlatform, canChangeCardLocation, isSuperAdmin, isClientAdmin, isLocationManager, canUseAdsFeature, hasPermission, isPartner } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        loadData();
    }, []);

    // v4.11.2 — Track Fleet Ops origin. When the modal closes/saves, we
    // bounce the operator straight back to Fleet Ops so they never have
    // to manually re-enter it. Filters and drawer state were already
    // stashed in sessionStorage by the Fleet drawer.
    const [returnToFleetOps, setReturnToFleetOps] = useState(false);
    const fleetReturnCardIdRef = useRef(null);

    // v4.11.1 — Fleet Ops deep-link. When arriving with `?card_id=...&from=fleet-ops`,
    // auto-open the Card Settings modal for that specific card so operators are
    // never dropped on the generic index after clicking through from Fleet Ops.
    useEffect(() => {
        if (!cards || cards.length === 0) return;
        const params = new URLSearchParams(window.location.search);
        const targetCardId = params.get('card_id');
        if (!targetCardId) return;
        const match = cards.find(
            (c) => c.card_id === targetCardId || c.id === targetCardId
        );
        if (match) {
            openLandingSettingsModal(match);
            if (params.get('return_to') === 'fleet-ops') {
                setReturnToFleetOps(true);
                fleetReturnCardIdRef.current = match.id;
            }
            // Strip the query param so a refresh doesn't re-open the modal.
            const next = new URL(window.location.href);
            next.searchParams.delete('card_id');
            next.searchParams.delete('from');
            next.searchParams.delete('return_to');
            window.history.replaceState({}, '', next.toString());
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cards]);

    // v4.11.2 — When the modal is dismissed and we arrived from Fleet Ops,
    // navigate back to /admin/card-fleet-ops. The Fleet page reads its
    // own sessionStorage stash to restore filters + reopen the drawer.
    useEffect(() => {
        if (returnToFleetOps && !isLandingSettingsModalOpen) {
            const cardId = fleetReturnCardIdRef.current;
            // Refresh the stash with the just-edited card so the drawer reopens.
            try {
                const prevRaw = sessionStorage.getItem('yot.fleet_ops.return_state');
                const prev = prevRaw ? JSON.parse(prevRaw) : {};
                sessionStorage.setItem(
                    'yot.fleet_ops.return_state',
                    JSON.stringify({
                        ...prev,
                        drawer_card_id: cardId,
                        stamped_at: Date.now(),
                    })
                );
            } catch { /* ignore */ }
            setReturnToFleetOps(false);
            fleetReturnCardIdRef.current = null;
            navigate('/admin/card-fleet-ops');
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [returnToFleetOps, isLandingSettingsModalOpen]);

    const loadData = async () => {
        try {
            const promises = [
                cardsAPI.list(),
                employeesAPI.list(),
                locationsAPI.list()
            ];

            // Super Admin can see all organizations
            if (isSuperAdmin()) {
                promises.push(organizationsAPI.list());
            }

            // v4.11.1 — Use allSettled so a single bad upstream (e.g.
            // /api/organizations Pydantic drift) does not blank the
            // entire page. Each slice falls back to an empty array.
            const results = await Promise.allSettled(promises);
            const pick = (i) => (results[i]?.status === 'fulfilled' ? results[i].value.data : []);
            setCards(sortByTagId(pick(0)));
            setEmployees(sortByName(pick(1)));
            setLocations(sortByName(pick(2)));

            if (isSuperAdmin() && results[3]) {
                setOrganizations(sortByName(pick(3)));
            }
            
            // Load organization info for platform lock status
            if (user?.organization_id) {
                try {
                    const orgRes = await organizationsAPI.get(user.organization_id);
                    setOrganization(orgRes.data);
                } catch (e) {
                    // Ignore - super admin may not have org
                }
            }
        } catch (error) {
            toast.error('Failed to load data');
        } finally {
            setLoading(false);
        }
    };

    // Get locations for a specific organization
    const getLocationsForOrg = (orgId) => {
        return locations.filter(loc => loc.organization_id === orgId);
    };

    // Get employees for a specific organization
    const getEmployeesForOrg = (orgId) => {
        return employees.filter(emp => emp.organization_id === orgId);
    };

    // AUTOMOTIVE cards resolve to /vehicle/{card_id}. A card is automotive
    // when its owning organization's vertical is 'automotive'. Detected from
    // the loaded org list (super admin), the single org record (client admin),
    // or the authenticated user's own vertical — so it works for every role.
    const isAutomotiveCard = (card) => {
        const oid = card?.organization_id;
        if (!oid) return false;
        if ((organizations || []).some(o => o?.id === oid && o?.vertical === 'automotive')) return true;
        if (organization?.id === oid && organization?.vertical === 'automotive') return true;
        if (user?.organization_id === oid && user?.organization_vertical === 'automotive') return true;
        return false;
    };
    const automotiveCardUrl = (card) => `${window.location.origin}/vehicle/${card.card_id}`;

    // MEDICAL — same detection, used only to offer RateMDs as a destination.
    const isMedicalCard = (card) => {
        const oid = card?.organization_id;
        if (!oid) return false;
        if ((organizations || []).some(o => o?.id === oid && o?.vertical === 'medical')) return true;
        if (organization?.id === oid && organization?.vertical === 'medical') return true;
        if (user?.organization_id === oid && user?.organization_vertical === 'medical') return true;
        return false;
    };

    // The server answers `permission_denied_change` when the tick is missing.
    const cardActionError = (error, fallback) => {
        const detail = error.response?.data?.detail;
        if (detail === 'permission_denied_change') {
            return t('errors.permissionDeniedChange', "You don't have permission to change this. Ask your administrator.");
        }
        return detail || fallback;
    };

    const handleAssign = async () => {
        if (!selectedCard) return;
        
        try {
            await cardsAPI.assign(selectedCard.id, selectedEmployee || null);
            toast.success(selectedEmployee ? 'Card assigned successfully' : 'Card unassigned');
            setIsAssignModalOpen(false);
            setSelectedCard(null);
            setSelectedEmployee('');
            loadData();
        } catch (error) {
            toast.error(cardActionError(error, 'Failed to assign card'));
        }
    };

    const handlePlatformChange = async (cardId, platform) => {
        try {
            await cardsAPI.updatePlatform(cardId, platform);
            toast.success('Platform updated');
            loadData();
        } catch (error) {
            toast.error(cardActionError(error, 'Failed to update platform'));
        }
    };

    const handleBulkPlatformChange = async () => {
        if (!bulkPlatform || selectedCards.length === 0) {
            toast.error('Please select cards and a platform');
            return;
        }

        try {
            const result = await cardsAPI.bulkUpdatePlatform(selectedCards, bulkPlatform);
            toast.success(result.data.message);
            if (result.data.errors?.length > 0) {
                result.data.errors.forEach(err => toast.error(err));
            }
            setIsBulkPlatformModalOpen(false);
            setSelectedCards([]);
            setBulkPlatform('');
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to update platforms');
        }
    };

    const handleEditOrganization = async () => {
        if (!selectedCard || !editOrganization || !editLocation) {
            toast.error('Please select organization and location');
            return;
        }

        try {
            await cardsAPI.updateOrganization(selectedCard.id, editOrganization, editLocation);
            toast.success('Card reassigned successfully');
            setIsEditOrgModalOpen(false);
            setSelectedCard(null);
            setEditOrganization('');
            setEditLocation('');
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to reassign card');
        }
    };

    const handleBulkOrgChange = async () => {
        if (!bulkOrganization || !bulkLocation || selectedCards.length === 0) {
            toast.error('Please select organization and location');
            return;
        }

        try {
            const result = await cardsAPI.bulkUpdateOrganization(selectedCards, bulkOrganization, bulkLocation);
            toast.success(result.data.message);
            if (result.data.errors?.length > 0) {
                result.data.errors.forEach(err => toast.error(err));
            }
            setIsBulkOrgModalOpen(false);
            setSelectedCards([]);
            setBulkOrganization('');
            setBulkLocation('');
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to reassign cards');
        }
    };

    const toggleCardSelection = (cardId) => {
        setSelectedCards(prev => 
            prev.includes(cardId) 
                ? prev.filter(id => id !== cardId)
                : [...prev, cardId]
        );
    };

    const selectAllCards = () => {
        if (selectedCards.length === filteredCards.length) {
            setSelectedCards([]);
        } else {
            setSelectedCards(filteredCards.map(c => c.id));
        }
    };

    const handleCreateCards = async () => {
        if (!newCardIds.trim() || !newCardLocation) {
            toast.error('Please fill in all required fields');
            return;
        }

        // For Super Admin, organization is required
        if (isSuperAdmin() && !newCardOrganization) {
            toast.error('Please select an organization');
            return;
        }

        const cardIdList = newCardIds.split(/[,\n]/).map(id => id.trim()).filter(id => id);
        if (cardIdList.length === 0) {
            toast.error('Please enter at least one card ID');
            return;
        }

        const location = locations.find(l => l.id === newCardLocation);
        if (!location) {
            toast.error('Invalid location');
            return;
        }

        // Use selected organization or location's organization
        const orgId = isSuperAdmin() ? newCardOrganization : location.organization_id;

        try {
            await cardsAPI.createBulk({
                card_ids: cardIdList,
                organization_id: orgId,
                location_id: newCardLocation,
                platform: newCardPlatform || undefined
            });
            toast.success(`${cardIdList.length} card(s) created successfully`);
            setIsCreateModalOpen(false);
            setNewCardIds('');
            setNewCardOrganization('');
            setNewCardLocation('');
            setNewCardPlatform('');
            setCreateTab('manual');
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to create cards');
        }
    };

    const handleCSVUpload = (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (e) => {
            const text = e.target?.result;
            if (typeof text === 'string') {
                const lines = text.split('\n');
                const cardIds = [];
                
                lines.forEach((line, index) => {
                    const columns = line.split(',');
                    const cardId = columns[0]?.trim();
                    if (index === 0 && (cardId.toLowerCase() === 'card_id' || cardId.toLowerCase() === 'cardid' || cardId.toLowerCase() === 'id')) {
                        return;
                    }
                    if (cardId && cardId.length > 0) {
                        cardIds.push(cardId);
                    }
                });
                
                setNewCardIds(cardIds.join('\n'));
                toast.success(`Loaded ${cardIds.length} card IDs from CSV`);
            }
        };
        reader.readAsText(file);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleDelete = async (cardId) => {
        if (!confirm('Are you sure you want to delete this card?')) return;
        
        try {
            await cardsAPI.delete(cardId);
            toast.success('Card deleted');
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to delete card');
        }
    };

    const openAssignModal = (card) => {
        setSelectedCard(card);
        setSelectedEmployee(card.employee_id || '');
        setIsAssignModalOpen(true);
    };

    const openEditOrgModal = (card) => {
        setSelectedCard(card);
        setEditOrganization(card.organization_id || '');
        setEditLocation(card.location_id || '');
        setIsEditOrgModalOpen(true);
    };

    const openEditNameModal = (card) => {
        setSelectedCard(card);
        setEditCardName(card.name || '');
        setIsEditNameModalOpen(true);
    };

    const openEditLocationModal = (card) => {
        setSelectedCard(card);
        setEditLocation(card.location_id || '');
        setIsEditLocationModalOpen(true);
    };

    const handleEditName = async () => {
        if (!selectedCard) return;
        
        try {
            await cardsAPI.updateName(selectedCard.id, editCardName);
            toast.success('Card name updated');
            setIsEditNameModalOpen(false);
            setSelectedCard(null);
            setEditCardName('');
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to update card name');
        }
    };

    const handleEditLocation = async () => {
        if (!selectedCard || !editLocation) {
            toast.error('Please select a location');
            return;
        }

        try {
            await cardsAPI.updateLocation(selectedCard.id, editLocation);
            toast.success('Card location updated');
            setIsEditLocationModalOpen(false);
            setSelectedCard(null);
            setEditLocation('');
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to update card location');
        }
    };

    const openLandingSettingsModal = (card) => {
        setSelectedCard(card);
        setLandingPageEnabled(card.landing_page_enabled || false);
        setLandingHeadline(card.landing_headline || '');
        setLandingDescription(card.landing_description || '');
        setLandingLogoUrl(card.landing_logo_url || '');
        setLandingLogoSize(card.landing_logo_size || 'medium');
        setLandingAutoRedirect(card.landing_auto_redirect !== false);
        setLandingRedirectDelay(card.landing_redirect_delay || 5);
        setCountdownClockEnabled(card.countdown_clock_enabled !== false);
        setCountdownClockColor(card.countdown_clock_color || 'blue');
        setLandingShowText(card.landing_show_text !== false);
        setShowYulontapBranding(card.show_yulontap_branding !== false);
        setShowLandingPreview(false);
        // Multiple Ads
        setMultipleAdsEnabled(card.multiple_ads_enabled || false);
        setAdImages(card.ad_images || []);
        setSameTextForAll(card.same_text_for_all !== false);
        setAdTexts(card.ad_texts || []);
        // Schedule
        setScheduleEnabled(card.schedule_enabled || false);
        setScheduleTimezone(card.schedule_timezone || 'America/New_York');
        setScheduleEntries(card.schedule_entries || []);
        setInteractionMode(card.interaction_mode || 'standard');
        setReviewShieldLogoUrl(card.review_shield_logo_url || '');
        setReviewShieldLogoSize(card.review_shield_logo_size || 'medium');
        setReviewShieldBackgroundImageUrl(card.review_shield_background_image_url || '');
        setCardStyle(card.card_style || '');
        setLanguageMode(card.language_mode || 'bilingual');

        // v3.3.1 — per-card override. null/undefined → inherit from org.
        setEnabledLanguages(Array.isArray(card.enabled_languages) ? card.enabled_languages : null);
        setLeadGenerationOverride(
            card.lead_generation_override && typeof card.lead_generation_override === 'object'
                ? card.lead_generation_override
                : null,
        );
        setGiftGeneratorSetupId(card.gift_generator_setup_id || null);
        setReviewShieldQuestions(card.review_shield_questions || []);
        setPublicReviewThreshold(card.public_review_threshold || 4);
        setPrivateReviewEnabled(card.private_review_enabled === true);
        // Experience Type is ORGANIZATION-WIDE — load it from the org, not
        // the card. The card's own experience_type is ignored (legacy).
        loadOrgExperienceType(card.organization_id);
        setGuestExperienceVersion(card.guest_experience_version === 'v2' ? 'v2' : 'v1');
        setTimingEnabled(card.timing_enabled === true);
        setTimingSchedule(Array.isArray(card.timing_schedule) ? card.timing_schedule : []);
        setNotificationEmails(card.notification_emails || []);
        setNotificationPhones(card.notification_phones || []);
        setDirectResponseEnabled(card.direct_response_enabled || false);
        setRequireEmployeePin(card.require_employee_pin !== false);
        setReviewCounterEnabled(card.review_counter_enabled || false);
        // P_TAP_PROTECTION_1.0 — hydrate canonical fields, falling back
        // to legacy values so pre-migration cards look identical.
        setTapProtectionEnabled(
            card.tap_protection_enabled !== undefined && card.tap_protection_enabled !== null
                ? !!card.tap_protection_enabled
                : (card.review_counter_enabled !== false),
        );
        setDuplicateProtectionEnabled(
            card.duplicate_protection_enabled !== undefined && card.duplicate_protection_enabled !== null
                ? !!card.duplicate_protection_enabled
                : (card.review_counter_enabled !== false),
        );
        setDuplicateWindowSeconds(
            card.duplicate_window_seconds ?? card.review_counter_window ?? 30,
        );
        setDwellProtectionEnabled(
            card.dwell_protection_enabled !== undefined && card.dwell_protection_enabled !== null
                ? !!card.dwell_protection_enabled
                : (card.review_counter_enabled !== false),
        );
        setDwellThresholdSeconds(
            card.dwell_threshold_seconds ?? card.review_counter_dwell_time ?? 10,
        );
        setTestMode(!!card.test_mode);
        setGiftCardEnabled(card.gift_card_enabled || false);
        setLoyalReviewEnabled(card.loyal_review_enabled || false);
        setLoyalReviewVoucherId(card.loyal_review_voucher_id || '');
        setLoyalReviewAutoFlow(card.loyal_review_auto_flow || false);
        setGiftGeneratorEnabled(card.gift_generator_enabled || false);
        setGlobalReviewEnabled(card.global_review_enabled || false);
        setUseReviewShieldWithGlobalReview(card.use_review_shield_with_global_review || false);
        setGlobalReviewDestinations(card.global_review_destinations || {});
        setGlobalReviewLogoUrl(card.global_review_logo_url || '');
        setGlobalReviewLogoSize(card.global_review_logo_size || 'medium');
        setGlobalReviewBackgroundImageUrl(card.global_review_background_image_url || '');
        setGlobalReviewMessageEn(card.global_review_message_en || '');

        setGlobalReviewMessageFr(card.global_review_message_fr || '');
        setHotelOpsLogoUrl(card.hotel_ops_logo_url || '');
        setHotelOpsLogoSize(card.hotel_ops_logo_size || 'medium');
        setHotelOpsBackgroundImageUrl(card.hotel_ops_background_image_url || '');
        setGuestServicesContext(card.guest_services_context || 'standard');
        setPoolDrinksEnabled(card.pool_drinks_enabled !== false);
        setPoolFoodEnabled(card.pool_food_enabled !== false);
        setPoolTowelsEnabled(card.pool_towels_enabled !== false);
        setPoolOtherEnabled(card.pool_other_enabled !== false);
        setPoolDrinkMenuImageUrl(card.pool_drink_menu_image_url || '');
        setPoolFoodMenuImageUrl(card.pool_food_menu_image_url || '');
        // Video / Media
        setLandingVideoEnabled(card.landing_video_enabled || false);
        setMediaType(card.media_type || null);
        setMediaVideoUrl(card.media_video_url || '');
        setMediaVideoId(card.media_video_id || '');
        setMediaVideoPosterUrl(card.media_video_poster_url || '');
        setMediaVideoFallbackImage(card.media_video_fallback_image || '');
        setMediaAutoplay(card.media_autoplay !== false);
        setMediaLoop(card.media_loop || false);
        setMediaMuted(card.media_muted !== false);
        setMediaSkipEnabled(card.media_skip_enabled !== false);
        setMediaSkipDelay(card.media_skip_delay || 5);
        // Capture snapshot for dirty detection — use card as source-of-truth
        setCardSettingsSnapshot(buildCardSettingsSnapshot(card));
        setIsLandingSettingsModalOpen(true);
    };

    // Single source of truth for card settings normalization (see utils).
    // Both snapshot and live state run through `serializeCardSettings` to
    // guarantee identical keys, types, and defaults — preventing phantom
    // "Pending Changes" bugs forever.
    const buildCardSettingsSnapshot = (card) => serializeCardSettings(card, 'card');

    const handleSaveLandingSettings = async () => {
        if (!selectedCard) return false;
        if (isSavingCardSettings) return false; // SAVING — prevent duplicate submits

        // Validate redirect delay
        let delay = parseInt(landingRedirectDelay) || 5;
        if (delay < 1) delay = 1;
        if (delay > 60) delay = 60;
        const skipDelay = parseInt(mediaSkipDelay) || 5;

        setIsSavingCardSettings(true);
        try {
            await cardsAPI.updateLandingSettings(selectedCard.id, {
                landing_page_enabled: landingPageEnabled,
                landing_headline: landingHeadline || null,
                landing_description: landingDescription || null,
                landing_logo_url: landingLogoUrl || null,
                landing_logo_size: landingLogoSize,
                landing_auto_redirect: landingAutoRedirect,
                landing_redirect_delay: delay,
                countdown_clock_enabled: countdownClockEnabled,
                countdown_clock_color: countdownClockColor,
                landing_show_text: landingShowText,
                show_yulontap_branding: showYulontapBranding,
                // Multiple Ads
                multiple_ads_enabled: multipleAdsEnabled,
                ad_images: adImages,
                same_text_for_all: sameTextForAll,
                ad_texts: adTexts,
                // Schedule
                schedule_enabled: scheduleEnabled,
                schedule_timezone: scheduleTimezone,
                schedule_entries: scheduleEntries,
                // Interaction Mode
                interaction_mode: interactionMode,
                // Review Shield specific
                review_shield_logo_url: reviewShieldLogoUrl || null,
                review_shield_logo_size: reviewShieldLogoSize,
                review_shield_background_image_url: reviewShieldBackgroundImageUrl || null,
                card_style: cardStyle || null,
                language_mode: languageMode,

                enabled_languages: enabledLanguages,  // v3.3.1 per-card override; null = inherit
                lead_generation_override: leadGenerationOverride,  // null = inherit org Gift Generator
                gift_generator_setup_id: giftGeneratorSetupId,      // null = org default setup
                review_shield_questions: reviewShieldQuestions || [],
                public_review_threshold: publicReviewThreshold || 4,
                private_review_enabled: privateReviewEnabled === true,
                // NOTE: experience_type is ORGANIZATION-WIDE and saved via its
                // own endpoint — intentionally NOT part of the card payload.
                // Guest Experience Version — presentation-only (v1 default)
                guest_experience_version: guestExperienceVersion === 'v2' ? 'v2' : 'v1',
                timing_enabled: !!timingEnabled,
                timing_schedule: timingSchedule || [],
                notification_emails: notificationEmails || [],
                notification_phones: notificationPhones || [],
                direct_response_enabled: directResponseEnabled,
                require_employee_pin: requireEmployeePin,
                review_counter_enabled: tapProtectionEnabled,
                review_counter_window: duplicateWindowSeconds,
                review_counter_dwell_time: dwellThresholdSeconds,
                // P_TAP_PROTECTION_1.0 — canonical Tap Protection fields
                tap_protection_enabled: tapProtectionEnabled,
                test_mode: testMode,
                duplicate_protection_enabled: duplicateProtectionEnabled,
                duplicate_window_seconds: duplicateWindowSeconds,
                dwell_protection_enabled: dwellProtectionEnabled,
                dwell_threshold_seconds: dwellThresholdSeconds,
                gift_card_enabled: giftCardEnabled,
                // Loyal Review — additive card-level fields
                loyal_review_enabled: loyalReviewEnabled,
                loyal_review_voucher_id: loyalReviewVoucherId || null,
                loyal_review_auto_flow: loyalReviewAutoFlow,
                gift_generator_enabled: giftGeneratorEnabled,
                // Global Review
                global_review_enabled: globalReviewEnabled,
                use_review_shield_with_global_review: useReviewShieldWithGlobalReview,
                global_review_destinations: globalReviewDestinations,
                global_review_logo_url: globalReviewLogoUrl || null,
                global_review_logo_size: globalReviewLogoSize || 'medium',
                global_review_background_image_url: globalReviewBackgroundImageUrl || null,
                global_review_message_en: globalReviewMessageEn || null,

                global_review_message_fr: globalReviewMessageFr || null,
                // Hotel Ops per-card branding (contextual — hotel_staff only)
                hotel_ops_logo_url: hotelOpsLogoUrl || null,
                hotel_ops_logo_size: hotelOpsLogoSize || 'medium',
                hotel_ops_background_image_url: hotelOpsBackgroundImageUrl || null,
                guest_services_context: guestServicesContext || 'standard',
                pool_drinks_enabled: poolDrinksEnabled,
                pool_food_enabled: poolFoodEnabled,
                pool_towels_enabled: poolTowelsEnabled,
                pool_other_enabled: poolOtherEnabled,
                pool_drink_menu_image_url: poolDrinkMenuImageUrl || null,
                pool_food_menu_image_url: poolFoodMenuImageUrl || null,
                // Video / Media
                landing_video_enabled: landingVideoEnabled,
                media_type: mediaType,
                media_video_url: mediaVideoUrl || null,
                media_video_id: mediaVideoId || null,
                media_video_poster_url: mediaVideoPosterUrl || null,
                media_video_fallback_image: mediaVideoFallbackImage || null,
                media_autoplay: mediaAutoplay,
                media_loop: mediaLoop,
                media_muted: mediaMuted,
                media_skip_enabled: mediaSkipEnabled,
                media_skip_delay: skipDelay
            });
            toast.success(t('common.saved'));
            // ── DIRTY → CLEAN transition (only on confirmed backend success) ──
            // The snapshot becomes the EXACT values that were just persisted:
            // the live state bag serialized through the same single-source-of-
            // truth serializer used for dirty detection. This replaces the old
            // hand-reconstructed `updatedCard` object, which had drifted from
            // the state bag (missing card_style, background images, tap
            // protection fields, and a media_skip_delay bug) and left the form
            // falsely DIRTY after every save.
            setLandingRedirectDelay(delay);
            setMediaSkipDelay(skipDelay);
            setCardSettingsSnapshot(serializeCardSettings(
                { ...cardSettingsStateBag, landingRedirectDelay: delay, mediaSkipDelay: skipDelay },
                'state'
            ));
            // Keep modal open so user can preview
            // Refresh card data in background
            loadData();
            return true;
        } catch (error) {
            // SAVE FAILED — keep the dirty state and the user's changes intact.
            const detail = error.response?.data?.detail;
            if (detail === 'loyal_review_voucher_org_mismatch') {
                toast.error(t('loyalReview.merchant.orgMismatch', "The selected reward belongs to another organization. Pick a voucher from this card's organization."));
            } else if (detail === 'loyal_review_voucher_invalid') {
                toast.error(t('loyalReview.merchant.invalidReward', 'The selected reward is no longer available. Pick another voucher.'));
            } else {
                toast.error(detail || 'Failed to update landing page settings');
            }
            return false;
        } finally {
            setIsSavingCardSettings(false);
        }
    };

    // ── Dirty detection: live serialization via single source of truth ──
    // `serializeCardSettings(state, 'state')` produces an identical JSON to
    // `serializeCardSettings(card, 'card')` for the same logical configuration.
    //
    // P_PORTAL_SAVE_INTEGRITY_AUDIT — the bag below MUST contain every key
    // listed in `CARD_SETTING_KEYS`. A missing key here = the field is invisible
    // to dirty-detection = "Save Changes" never enables for that field, and
    // the user's change silently never persists. The dev-time invariant
    // `assertSerializerCoverage` (see utils/serializeCardSettings.js) screams
    // in the browser console whenever this bag drifts from the registry.
    // ─── Review Shield Experience Type — ORGANIZATION-WIDE ───────────────
    // One value per organization that governs every Review Shield card in
    // that org. Loaded/saved independently of the card's "Save Changes"
    // button. (Distinct from a "Saved Review Shield Experience" snapshot.)
    const loadOrgExperienceType = useCallback(async (orgId) => {
        setRsExperienceOrgId(orgId || null);
        if (!orgId) { setExperienceType('dine_in'); return; }
        try {
            const res = await api.get('/review-shield/experience-type', { params: { organization_id: orgId } });
            setExperienceType(res.data?.experience_type || 'dine_in');
        } catch (err) {
            console.error('Failed to load org experience type:', err);
            setExperienceType('dine_in');
        }
    }, []);

    const handleExperienceTypeChange = useCallback(async (value) => {
        const orgId = rsExperienceOrgId || selectedCard?.organization_id;
        const previous = experienceType;
        setExperienceType(value); // optimistic
        if (!orgId) { toast.error(t('common.error', 'Something went wrong')); return; }
        setSavingExperienceType(true);
        try {
            await api.put('/review-shield/experience-type', { experience_type: value }, { params: { organization_id: orgId } });
            toast.success(t('reviewShield.experience.savedOrgWide', 'Experience Type saved for the whole organization'));
        } catch (err) {
            console.error('Failed to save org experience type:', err);
            setExperienceType(previous); // rollback
            toast.error(t('reviewShield.experience.saveFailed', 'Could not save Experience Type'));
        } finally {
            setSavingExperienceType(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rsExperienceOrgId, selectedCard, experienceType]);

    const cardSettingsStateBag = {
        interactionMode, landingPageEnabled, landingShowText, landingHeadline, landingDescription,
        landingLogoUrl, landingLogoSize, landingAutoRedirect, landingRedirectDelay,
        countdownClockEnabled, countdownClockColor, showYulontapBranding,
        multipleAdsEnabled, adImages, sameTextForAll, adTexts,
        scheduleEnabled, scheduleTimezone, scheduleEntries,
        reviewShieldLogoUrl, reviewShieldLogoSize, reviewShieldBackgroundImageUrl,
        languageMode,
        enabledLanguages,         // v3.3.1 per-card override — added to dirty detection v4.40.3
        leadGenerationOverride,
        giftGeneratorSetupId,
        reviewShieldQuestions,
        cardStyle,
        publicReviewThreshold, privateReviewEnabled, timingEnabled, timingSchedule,
        guestExperienceVersion,
        notificationEmails, notificationPhones, directResponseEnabled, requireEmployeePin,
        reviewCounterEnabled, giftCardEnabled,
        loyalReviewEnabled, loyalReviewVoucherId, loyalReviewAutoFlow, giftGeneratorEnabled,
        tapProtectionEnabled, duplicateProtectionEnabled, duplicateWindowSeconds,
        dwellProtectionEnabled, dwellThresholdSeconds, testMode,
        globalReviewEnabled, useReviewShieldWithGlobalReview, globalReviewDestinations,
        globalReviewLogoUrl, globalReviewLogoSize, globalReviewBackgroundImageUrl,
        globalReviewMessageEn, globalReviewMessageFr,
        hotelOpsLogoUrl, hotelOpsLogoSize, hotelOpsBackgroundImageUrl,
        guestServicesContext, poolDrinksEnabled, poolFoodEnabled, poolTowelsEnabled,
        poolOtherEnabled, poolDrinkMenuImageUrl, poolFoodMenuImageUrl,
        landingVideoEnabled, mediaType, mediaVideoUrl, mediaVideoId,
        mediaVideoPosterUrl, mediaVideoFallbackImage, mediaAutoplay, mediaLoop, mediaMuted,
        mediaSkipEnabled, mediaSkipDelay,
    };
    const currentCardSettings = useMemo(() => serializeCardSettings(cardSettingsStateBag, 'state'), [
        interactionMode, landingPageEnabled, landingShowText, landingHeadline, landingDescription,
        landingLogoUrl, landingLogoSize, landingAutoRedirect, landingRedirectDelay,
        countdownClockEnabled, countdownClockColor, showYulontapBranding,

        multipleAdsEnabled, adImages, sameTextForAll, adTexts,
        scheduleEnabled, scheduleTimezone, scheduleEntries,
        reviewShieldLogoUrl, reviewShieldLogoSize, reviewShieldBackgroundImageUrl,
        languageMode,
        enabledLanguages,         // P_PORTAL_SAVE_INTEGRITY_AUDIT: missing dep was the bug
        leadGenerationOverride,
        giftGeneratorSetupId,
        reviewShieldQuestions,
        cardStyle,
        publicReviewThreshold, privateReviewEnabled, timingEnabled, timingSchedule,
        guestExperienceVersion,
        notificationEmails, notificationPhones, directResponseEnabled, requireEmployeePin,
        reviewCounterEnabled, giftCardEnabled,
        loyalReviewEnabled, loyalReviewVoucherId, loyalReviewAutoFlow, giftGeneratorEnabled,
        tapProtectionEnabled, duplicateProtectionEnabled, duplicateWindowSeconds,
        dwellProtectionEnabled, dwellThresholdSeconds, testMode,
        globalReviewEnabled, useReviewShieldWithGlobalReview, globalReviewDestinations,
        globalReviewLogoUrl, globalReviewLogoSize, globalReviewBackgroundImageUrl,
        globalReviewMessageEn, globalReviewMessageFr,
        hotelOpsLogoUrl, hotelOpsLogoSize, hotelOpsBackgroundImageUrl,
        guestServicesContext, poolDrinksEnabled, poolFoodEnabled, poolTowelsEnabled,
        poolOtherEnabled, poolDrinkMenuImageUrl, poolFoodMenuImageUrl,
        landingVideoEnabled, mediaType, mediaVideoUrl, mediaVideoId,
        mediaVideoPosterUrl, mediaVideoFallbackImage, mediaAutoplay, mediaLoop, mediaMuted,
        mediaSkipEnabled, mediaSkipDelay,
    ]);


    const isCardSettingsDirty = cardSettingsSnapshot !== null && currentCardSettings !== cardSettingsSnapshot;

    // Build a human-readable diff of pending card-setting changes for the preview tooltip
    const cardSettingsDiff = useMemo(() => {
        if (!isCardSettingsDirty || !cardSettingsSnapshot) return [];
        let prev;
        try { prev = JSON.parse(cardSettingsSnapshot); } catch { return []; }
        let curr;
        try { curr = JSON.parse(currentCardSettings); } catch { return []; }

        const yesNo = (v) => v ? t('common.yes') : t('common.no');
        const onOff = (v) => v ? t('manager.on') : t('manager.off');
        const fmt = (v) => {
            if (v === null || v === undefined || v === '') return '—';
            if (typeof v === 'boolean') return onOff(v);
            if (Array.isArray(v)) return `${v.length} ${t('common.item', { count: v.length, defaultValue: v.length === 1 ? 'item' : 'items' })}`;
            if (typeof v === 'object') return t('common.edited', 'edited');
            return String(v);
        };

        // Field → label map (human-readable names)
        const FIELDS = [
            ['interactionMode', t('cardSettings.interactionMode', 'Interaction Mode'), (v) => v],
            ['landingPageEnabled', t('cardSettings.landingPageEnabled', 'Landing Page'), onOff],
            ['landingShowText', t('cardSettings.landingShowText', 'Show Text'), onOff],
            ['landingHeadline', t('cardSettings.headline', 'Headline'), fmt],
            ['landingDescription', t('cardSettings.description', 'Description'), fmt],
            ['landingLogoUrl', t('cardSettings.landingLogo', 'Landing Logo'), fmt],
            ['landingLogoSize', t('cardSettings.logoSize', 'Logo Size'), fmt],
            ['landingAutoRedirect', t('cardSettings.autoRedirect', 'Auto Redirect'), onOff],
            ['landingRedirectDelay', t('cardSettings.redirectDelay', 'Redirect Delay'), (v) => `${v}s`],
            ['countdownClockEnabled', t('cardSettings.countdownClock', 'Countdown Clock'), onOff],
            ['countdownClockColor', t('cardSettings.countdownColor', 'Countdown Color'), fmt],
            ['showYulontapBranding', t('cardSettings.yulontapBranding', 'YulOnTap Branding'), onOff],
            ['multipleAdsEnabled', t('cardSettings.multipleAds', 'Multiple Ads'), onOff],
            ['adImages', t('cardSettings.adImages', 'Ad Images'), fmt],
            ['sameTextForAll', t('cardSettings.sameTextForAll', 'Same Text'), onOff],
            ['adTexts', t('cardSettings.adTexts', 'Ad Texts'), fmt],
            ['scheduleEnabled', t('cardSettings.scheduleEnabled', 'Schedule'), onOff],
            ['scheduleTimezone', t('cardSettings.scheduleTimezone', 'Timezone'), fmt],
            ['scheduleEntries', t('cardSettings.scheduleEntries', 'Schedule Entries'), fmt],
            ['reviewShieldLogoUrl', t('cardSettings.reviewShieldLogo', 'Shield Logo'), fmt],
            ['reviewShieldLogoSize', t('cardSettings.reviewShieldLogoSize', 'Shield Logo Size'), fmt],
            ['languageMode', t('cardSettings.languageMode', 'Language Mode'), fmt],
            ['reviewShieldQuestions', t('cardSettings.reviewShieldQuestions', 'Shield Questions'), fmt],
            ['publicReviewThreshold', t('cardSettings.publicReviewThreshold', 'Public Review Threshold'), (v) => `${v}★`],
            ['privateReviewEnabled', t('reviewShield.privateReview', 'Private Review'), onOff],
            ['timingEnabled', t('timing.title', 'Timing'), onOff],
            ['timingSchedule', t('cardSettings.timingSchedule', 'Timing Schedule'), fmt],
            ['notificationEmails', t('cardSettings.notificationEmails', 'Email Recipients'), fmt],
            ['notificationPhones', t('cardSettings.notificationPhones', 'SMS Recipients'), fmt],
            ['directResponseEnabled', t('cardSettings.directResponse', 'Direct Response'), onOff],
            ['requireEmployeePin', t('cardSettings.requireEmployeePin', 'Employee PIN'), onOff],
            ['reviewCounterEnabled', t('cardSettings.reviewCounter', 'Review Counter'), onOff],
            ['giftCardEnabled', t('cardSettings.giftCard', 'Gift Cards'), onOff],
            ['loyalReviewEnabled', t('cardSettings.loyalReviewEnabled', 'Loyal Review'), onOff],
            ['loyalReviewVoucherId', t('cardSettings.loyalReviewVoucherId', 'Loyal Review Reward'), fmt],
            ['loyalReviewAutoFlow', t('loyalReview.merchant.autoFlowLabel', 'Auto Flow'), onOff],
            ['giftGeneratorEnabled', t('giftGenerator.cardLabel', 'Gift Generator'), onOff],
            ['giftGeneratorSetupId', t('giftSetups.cardSectionTitle', 'Gift Generator setup'),
                (v) => (v ? String(v).slice(0, 8) : t('giftSetups.cardUseDefault', 'Organization default'))],
            ['globalReviewEnabled', t('cardSettings.globalReview', 'Global Review'), onOff],
            ['useReviewShieldWithGlobalReview', t('cardSettings.useReviewShieldWithGR', 'Shield + Global Review'), onOff],
            ['globalReviewDestinations', t('cardSettings.globalReviewDestinations', 'Global Review Platforms'), fmt],
            ['globalReviewLogoUrl', t('cardSettings.globalReviewLogo', 'Global Review Logo'), fmt],
            ['globalReviewLogoSize', t('cardSettings.globalReviewLogoSize', 'Global Review Logo Size'), fmt],
            ['globalReviewMessageEn', t('cardSettings.globalReviewMessageEn', 'Global Review Message (EN)'), fmt],
            ['globalReviewMessageFr', t('cardSettings.globalReviewMessageFr', 'Global Review Message (FR)'), fmt],
            ['hotelOpsLogoUrl', t('cardSettings.hotelOpsLogo', 'Hotel Ops Logo'), fmt],
            ['hotelOpsLogoSize', t('cardSettings.hotelOpsLogoSize', 'Hotel Ops Logo Size'), fmt],
            ['hotelOpsBackgroundImageUrl', t('cardSettings.hotelOpsWallpaper', 'Hotel Ops Wallpaper'), fmt],
            ['landingVideoEnabled', t('cardSettings.landingVideo', 'Landing Video'), onOff],
            ['mediaType', t('cardSettings.mediaType', 'Media Type'), fmt],
            ['mediaVideoUrl', t('cardSettings.mediaVideoUrl', 'Video URL'), fmt],
            ['mediaVideoId', t('cardSettings.mediaVideoId', 'Video ID'), fmt],
            ['mediaVideoPosterUrl', t('cardSettings.mediaVideoPoster', 'Video Poster'), fmt],
            ['mediaVideoFallbackImage', t('cardSettings.mediaVideoFallback', 'Video Fallback'), fmt],
            ['mediaAutoplay', t('cardSettings.mediaAutoplay', 'Autoplay'), onOff],
            ['mediaLoop', t('cardSettings.mediaLoop', 'Loop'), onOff],
            ['mediaMuted', t('cardSettings.mediaMuted', 'Muted'), onOff],
            ['mediaSkipEnabled', t('cardSettings.mediaSkip', 'Allow Skip'), onOff],
            ['mediaSkipDelay', t('cardSettings.mediaSkipDelay', 'Skip Delay'), (v) => `${v}s`],
        ];

        const changes = [];
        for (const [key, label, formatter] of FIELDS) {
            const a = JSON.stringify(prev[key]);
            const b = JSON.stringify(curr[key]);
            if (a !== b) {
                changes.push({
                    key,
                    label,
                    before: formatter(prev[key]),
                    after: formatter(curr[key]),
                });
            }
        }
        return changes;
    }, [isCardSettingsDirty, cardSettingsSnapshot, currentCardSettings, t]);

    // Guarded close for CardSettingsModal — warn on unsaved changes
    const requestCloseCardSettings = useCallback((open) => {
        if (open) {
            setIsLandingSettingsModalOpen(true);
            return;
        }
        if (isCardSettingsDirty) {
            pendingCloseRef.current = () => {
                setIsLandingSettingsModalOpen(false);
                setCardSettingsSnapshot(null);
            };
            setCardSettingsExitGuard(true);
        } else {
            setIsLandingSettingsModalOpen(false);
            setCardSettingsSnapshot(null);
        }
    }, [isCardSettingsDirty]);

    const handleCardSettingsExitDiscard = () => {
        setCardSettingsExitGuard(false);
        const act = pendingCloseRef.current;
        pendingCloseRef.current = null;
        if (act) act();
    };
    const handleCardSettingsExitStay = () => {
        setCardSettingsExitGuard(false);
        pendingCloseRef.current = null;
    };
    const handleCardSettingsExitSave = async () => {
        const ok = await handleSaveLandingSettings();
        setCardSettingsExitGuard(false);
        if (!ok) {
            // SAVE FAILED — stay in the modal, keep changes + dirty state intact
            pendingCloseRef.current = null;
            return;
        }
        const act = pendingCloseRef.current;
        pendingCloseRef.current = null;
        if (act) setTimeout(act, 100);
    };

    // Browser-level unsaved-changes guard (refresh / tab close while modal is open)
    useEffect(() => {
        if (!isLandingSettingsModalOpen || !isCardSettingsDirty) return;
        const onBeforeUnload = (e) => { e.preventDefault(); e.returnValue = ''; return ''; };
        window.addEventListener('beforeunload', onBeforeUnload);
        return () => window.removeEventListener('beforeunload', onBeforeUnload);
    }, [isLandingSettingsModalOpen, isCardSettingsDirty]);

    const handleLogoUpload = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        // Validate file type
        const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp', 'image/svg+xml'];
        if (!allowedTypes.includes(file.type)) {
            toast.error('Please upload a PNG, JPEG, GIF, WebP, or SVG file');
            return;
        }

        // Validate file size (5MB max)
        if (file.size > 5 * 1024 * 1024) {
            toast.error('File is too large. Maximum size is 5MB');
            return;
        }

        setUploadingLogo(true);
        try {
            const response = await uploadAPI.uploadLogo(file);
            const fullUrl = `${window.location.origin}${response.data.url}`;
            setLandingLogoUrl(fullUrl);
            toast.success('Logo uploaded successfully');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to upload logo');
        } finally {
            setUploadingLogo(false);
            if (logoInputRef.current) {
                logoInputRef.current.value = '';
            }
        }
    };

    const clearLogo = () => {
        setLandingLogoUrl('');
    };

    const clearReviewShieldLogo = () => {
        setReviewShieldLogoUrl('');
    };

    const handleReviewShieldLogoUpload = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp', 'image/svg+xml'];
        if (!allowedTypes.includes(file.type)) {
            toast.error('Please upload a PNG, JPEG, GIF, WebP, or SVG file');
            return;
        }

        if (file.size > 5 * 1024 * 1024) {
            toast.error('File is too large. Maximum size is 5MB');
            return;
        }

        setUploadingLogo(true);
        try {
            const response = await uploadAPI.uploadLogo(file);
            const fullUrl = `${window.location.origin}${response.data.url}`;
            setReviewShieldLogoUrl(fullUrl);
            toast.success('Logo uploaded successfully');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to upload logo');
        } finally {
            setUploadingLogo(false);
            if (reviewShieldLogoInputRef.current) {
                reviewShieldLogoInputRef.current.value = '';
            }
        }
    };

    const handleGlobalReviewLogoUpload = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp', 'image/svg+xml'];
        if (!allowedTypes.includes(file.type)) {
            toast.error('Please upload a PNG, JPEG, GIF, WebP, or SVG file');
            return;
        }

        if (file.size > 5 * 1024 * 1024) {
            toast.error('File is too large. Maximum size is 5MB');
            return;
        }

        setUploadingLogo(true);
        try {
            const response = await uploadAPI.uploadLogo(file);
            const fullUrl = `${window.location.origin}${response.data.url}`;
            setGlobalReviewLogoUrl(fullUrl);
            toast.success('Global Review logo uploaded');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to upload logo');
        } finally {
            setUploadingLogo(false);
            if (globalReviewLogoInputRef.current) {
                globalReviewLogoInputRef.current.value = '';
            }
        }
    };

    // v3.5.18 — Wallpaper upload helpers (Review Shield + Global Review).
    // Reuses uploadAPI.uploadLogo because the underlying endpoint accepts any
    // image. Larger size cap (8MB) since wallpapers tend to be photos.
    const _uploadWallpaper = async (file, setUrl, label) => {
        const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
        if (!allowedTypes.includes(file.type)) {
            toast.error('Please upload a PNG, JPEG, GIF, or WebP file');
            return;
        }
        if (file.size > 8 * 1024 * 1024) {
            toast.error('File is too large. Maximum size is 8MB');
            return;
        }
        setUploadingLogo(true);
        try {
            const response = await uploadAPI.uploadLogo(file);
            const fullUrl = `${window.location.origin}${response.data.url}`;
            setUrl(fullUrl);
            toast.success(`${label} wallpaper uploaded`);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to upload wallpaper');
        } finally {
            setUploadingLogo(false);
        }
    };

    const handleReviewShieldWallpaperUpload = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;
        await _uploadWallpaper(file, setReviewShieldBackgroundImageUrl, 'Review Shield');
        if (reviewShieldWallpaperInputRef.current) reviewShieldWallpaperInputRef.current.value = '';
    };

    const handleGlobalReviewWallpaperUpload = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;
        await _uploadWallpaper(file, setGlobalReviewBackgroundImageUrl, 'Global Review');
        if (globalReviewWallpaperInputRef.current) globalReviewWallpaperInputRef.current.value = '';
    };

    // Hotel Ops branding — reuses the exact per-card logo + wallpaper upload path.
    const clearHotelOpsLogo = () => setHotelOpsLogoUrl('');
    const handleHotelOpsLogoUpload = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;
        const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp', 'image/svg+xml'];
        if (!allowedTypes.includes(file.type)) { toast.error('Please upload a PNG, JPEG, GIF, WebP, or SVG file'); return; }
        if (file.size > 5 * 1024 * 1024) { toast.error('File is too large. Maximum size is 5MB'); return; }
        setUploadingLogo(true);
        try {
            const response = await uploadAPI.uploadLogo(file);
            setHotelOpsLogoUrl(`${window.location.origin}${response.data.url}`);
            toast.success('Logo uploaded successfully');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to upload logo');
        } finally {
            setUploadingLogo(false);
            if (hotelOpsLogoInputRef.current) hotelOpsLogoInputRef.current.value = '';
        }
    };
    const handleHotelOpsWallpaperUpload = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;
        await _uploadWallpaper(file, setHotelOpsBackgroundImageUrl, 'Hotel Ops');
        if (hotelOpsWallpaperInputRef.current) hotelOpsWallpaperInputRef.current.value = '';
    };

    // Pool & Terrace menu images — reuse the existing image upload path.
    const handlePoolDrinkMenuUpload = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;
        await _uploadWallpaper(file, setPoolDrinkMenuImageUrl, 'Drink Menu');
        if (poolDrinkMenuInputRef.current) poolDrinkMenuInputRef.current.value = '';
    };
    const handlePoolFoodMenuUpload = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;
        await _uploadWallpaper(file, setPoolFoodMenuImageUrl, 'Food Menu');
        if (poolFoodMenuInputRef.current) poolFoodMenuInputRef.current.value = '';
    };

    // Handle ad image upload for multiple ads
    const handleAdImageUpload = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        if (adImages.length >= 5) {
            toast.error('Maximum 5 images allowed');
            return;
        }

        const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp', 'image/svg+xml'];
        if (!allowedTypes.includes(file.type)) {
            toast.error('Please upload a PNG, JPEG, GIF, WebP, or SVG file');
            return;
        }

        if (file.size > 5 * 1024 * 1024) {
            toast.error('File is too large. Maximum size is 5MB');
            return;
        }

        setUploadingAdImage(true);
        try {
            const response = await uploadAPI.uploadLogo(file);
            const fullUrl = `${window.location.origin}${response.data.url}`;
            setAdImages([...adImages, fullUrl]);
            // Add empty text entry if using individual texts
            if (!sameTextForAll) {
                setAdTexts([...adTexts, { headline: '', description: '' }]);
            }
            toast.success('Image uploaded');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to upload image');
        } finally {
            setUploadingAdImage(false);
            if (adImageInputRef.current) {
                adImageInputRef.current.value = '';
            }
        }
    };

    const removeAdImage = (index) => {
        const newImages = adImages.filter((_, i) => i !== index);
        setAdImages(newImages);
        if (!sameTextForAll) {
            const newTexts = adTexts.filter((_, i) => i !== index);
            setAdTexts(newTexts);
        }
    };

    const updateAdText = (index, field, value) => {
        const newTexts = [...adTexts];
        if (!newTexts[index]) {
            newTexts[index] = { headline: '', description: '' };
        }
        newTexts[index][field] = value;
        setAdTexts(newTexts);
    };

    // Handle video upload for landing page media
    const handleVideoUpload = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        const allowedTypes = ['video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/webm', 'video/mpeg'];
        if (!allowedTypes.includes(file.type) && !file.name.match(/\.(mp4|mov|avi|webm|mpeg)$/i)) {
            toast.error('Please upload an MP4, MOV, AVI, or WebM video file');
            return;
        }

        if (file.size > 50 * 1024 * 1024) {
            toast.error('Video is too large. Maximum size is 50MB.');
            return;
        }

        setUploadingVideo(true);
        setVideoUploadProgress(0);
        try {
            const response = await uploadAPI.uploadVideo(file, (progressEvent) => {
                if (progressEvent.processing) {
                    setVideoUploadProgress(100); // Shows "Processing video..."
                } else {
                    const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    setVideoUploadProgress(Math.min(percent, 99));
                }
            });
            const data = response.data;
            setMediaVideoUrl(data.url);
            setMediaVideoId(data.id);
            setMediaVideoPosterUrl(data.poster_url || '');
            setMediaType('video');
            setLandingVideoEnabled(true);
            if (data.duration_warning) {
                toast.info(data.duration_warning);
            }
            toast.success(`Video uploaded (${(data.size / 1024).toFixed(0)}KB, ${data.compression_ratio}% of original)`);
        } catch (error) {
            console.error('Video upload error:', error);
            const detail = error.response?.data?.detail;
            const status = error.response?.status;
            if (detail) {
                toast.error(detail);
            } else if (status) {
                toast.error(`Upload failed (HTTP ${status}). ${error.response?.data?.message || ''}`);
            } else {
                // No response at all — log everything we know
                const errMsg = error.message || 'Unknown error';
                console.error('Upload error details:', {
                    message: errMsg,
                    code: error.code,
                    response: error.response,
                    request: error.request ? 'exists' : 'missing'
                });
                toast.error(`Upload failed: ${errMsg}`);
            }
        } finally {
            setUploadingVideo(false);
            setVideoUploadProgress(0);
            if (videoInputRef.current) {
                videoInputRef.current.value = '';
            }
        }
    };

    const removeVideo = () => {
        setMediaVideoUrl('');
        setMediaVideoId('');
        setMediaVideoPosterUrl('');
        setMediaType(null);
        setLandingVideoEnabled(false);
    };

    // Bulk apply landing settings
    const handleBulkApply = async () => {
        if (!bulkApplyOrg && !bulkApplyLocation) {
            toast.error('Please select an organization or location');
            return;
        }

        setBulkApplying(true);
        try {
            let delay = parseInt(landingRedirectDelay) || 5;
            if (delay < 1) delay = 1;
            if (delay > 60) delay = 60;

            // v4.34.6 — Parity with the single-card save payload so wallpapers,
            // logos, Global Review settings, card style, languages, video /
            // media, and PIN/counter settings all propagate. The previous
            // payload was missing ~15 fields, which caused the production
            // demo failure where a Review Shield wallpaper uploaded to ONE
            // card stuck to that card only and the bulk apply silently
            // wiped (or never wrote) the field on every other matching card.
            const response = await api.post('/cards/bulk-landing-settings', {
                settings: {
                    landing_page_enabled: landingPageEnabled,
                    landing_headline: landingHeadline || null,
                    landing_description: landingDescription || null,
                    landing_logo_url: landingLogoUrl || null,
                    landing_logo_size: landingLogoSize,
                    landing_auto_redirect: landingAutoRedirect,
                    landing_redirect_delay: delay,
                    countdown_clock_enabled: countdownClockEnabled,
                    countdown_clock_color: countdownClockColor,
                    landing_show_text: landingShowText,
                    show_yulontap_branding: showYulontapBranding,
                    // Multiple Ads
                    multiple_ads_enabled: multipleAdsEnabled,
                    ad_images: adImages,
                    same_text_for_all: sameTextForAll,
                    ad_texts: adTexts,
                    // Schedule
                    schedule_enabled: scheduleEnabled,
                    schedule_timezone: scheduleTimezone,
                    schedule_entries: scheduleEntries,
                    // Interaction Mode
                    interaction_mode: interactionMode,
                    // Review Shield (logo, size + wallpaper)
                    review_shield_logo_url: reviewShieldLogoUrl || null,
                    review_shield_logo_size: reviewShieldLogoSize,
                    review_shield_background_image_url: reviewShieldBackgroundImageUrl || null,
                    // Card surface style + per-card language override
                    card_style: cardStyle || null,
                    language_mode: languageMode,
                    enabled_languages: enabledLanguages,
                    lead_generation_override: leadGenerationOverride,
                    gift_generator_setup_id: giftGeneratorSetupId,
                    review_shield_questions: reviewShieldQuestions || [],
                    public_review_threshold: publicReviewThreshold || 4,
                    private_review_enabled: privateReviewEnabled === true,
                    // Guest Experience Version — presentation-only (v1 default)
                    guest_experience_version: guestExperienceVersion === 'v2' ? 'v2' : 'v1',
                    // Timing
                    timing_enabled: !!timingEnabled,
                    timing_schedule: timingSchedule || [],
                    // Notifications
                    notification_emails: notificationEmails || [],
                    notification_phones: notificationPhones || [],
                    // Operational toggles
                    direct_response_enabled: directResponseEnabled,
                    require_employee_pin: requireEmployeePin,
                    review_counter_enabled: tapProtectionEnabled,
                    review_counter_window: duplicateWindowSeconds,
                    review_counter_dwell_time: dwellThresholdSeconds,
                    // P_TAP_PROTECTION_1.0 — canonical Tap Protection fields
                    tap_protection_enabled: tapProtectionEnabled,
                    test_mode: testMode,
                test_mode: testMode,
                    duplicate_protection_enabled: duplicateProtectionEnabled,
                    duplicate_window_seconds: duplicateWindowSeconds,
                    dwell_protection_enabled: dwellProtectionEnabled,
                    dwell_threshold_seconds: dwellThresholdSeconds,
                    gift_card_enabled: giftCardEnabled,
                    // Loyal Review — additive card-level fields
                    loyal_review_enabled: loyalReviewEnabled,
                    loyal_review_voucher_id: loyalReviewVoucherId || null,
                    loyal_review_auto_flow: loyalReviewAutoFlow,
                    gift_generator_enabled: giftGeneratorEnabled,
                    // Global Review (master + sub-settings + branding + messages)
                    global_review_enabled: globalReviewEnabled,
                    use_review_shield_with_global_review: useReviewShieldWithGlobalReview,
                    global_review_destinations: globalReviewDestinations,
                    global_review_logo_url: globalReviewLogoUrl || null,
                    global_review_logo_size: globalReviewLogoSize || 'medium',
                    global_review_background_image_url: globalReviewBackgroundImageUrl || null,
                    global_review_message_en: globalReviewMessageEn || null,
                    global_review_message_fr: globalReviewMessageFr || null,
                    // Hotel Ops per-card branding (contextual — hotel_staff only)
                    hotel_ops_logo_url: hotelOpsLogoUrl || null,
                    hotel_ops_logo_size: hotelOpsLogoSize || 'medium',
                    hotel_ops_background_image_url: hotelOpsBackgroundImageUrl || null,
                    guest_services_context: guestServicesContext || 'standard',
                    pool_drinks_enabled: poolDrinksEnabled,
                    pool_food_enabled: poolFoodEnabled,
                    pool_towels_enabled: poolTowelsEnabled,
                    pool_other_enabled: poolOtherEnabled,
                    pool_drink_menu_image_url: poolDrinkMenuImageUrl || null,
                    pool_food_menu_image_url: poolFoodMenuImageUrl || null,
                    // Video / Media
                    landing_video_enabled: landingVideoEnabled,
                    media_type: mediaType,
                    media_video_url: mediaVideoUrl || null,
                    media_video_id: mediaVideoId || null,
                    media_video_poster_url: mediaVideoPosterUrl || null,
                    media_video_fallback_image: mediaVideoFallbackImage || null,
                    media_autoplay: mediaAutoplay,
                    media_loop: mediaLoop,
                    media_muted: mediaMuted,
                    media_skip_enabled: mediaSkipEnabled,
                    media_skip_delay: parseInt(mediaSkipDelay) || 5,
                },
                organization_id: bulkApplyOrg || null,
                location_id: bulkApplyLocation || null
            });
            toast.success(response.data.message);
            setShowBulkApplyModal(false);
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to bulk apply settings');
        } finally {
            setBulkApplying(false);
        }
    };

    // Schedule helper functions
    const addScheduleEntry = () => {
        setScheduleEntries([
            ...scheduleEntries,
            {
                name: `Time Block ${scheduleEntries.length + 1}`,
                days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
                start_time: '09:00',
                end_time: '17:00'
            }
        ]);
    };

    const removeScheduleEntry = (index) => {
        setScheduleEntries(scheduleEntries.filter((_, i) => i !== index));
    };

    const updateScheduleEntry = (index, field, value) => {
        const updated = [...scheduleEntries];
        updated[index] = { ...updated[index], [field]: value };
        setScheduleEntries(updated);
    };

    const toggleScheduleDay = (entryIndex, day) => {
        const updated = [...scheduleEntries];
        const days = updated[entryIndex].days || [];
        if (days.includes(day)) {
            updated[entryIndex].days = days.filter(d => d !== day);
        } else {
            updated[entryIndex].days = [...days, day];
        }
        setScheduleEntries(updated);
    };

    // Filter cards
    const filteredCards = cards.filter(card => {
        const matchesSearch = card.card_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
            card.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            card.employee_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            card.organization_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            card.location_name?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus = filterStatus === 'all' || card.status === filterStatus;
        const matchesLocation = filterLocation === 'all' || card.location_id === filterLocation;
        const matchesOrg = filterOrganization === 'all' || card.organization_id === filterOrganization;
        return matchesSearch && matchesStatus && matchesLocation && matchesOrg;
    });

    const getStatusBadge = (status) => {
        switch (status) {
            case 'active':
                return <Badge className="bg-emerald-100 text-emerald-700">{t('common.active')}</Badge>;
            case 'inactive':
                return <Badge className="bg-red-100 text-red-700">{t('common.inactive')}</Badge>;
            default:
                return <Badge className="bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300">{t('cards.unassigned')}</Badge>;
        }
    };

    const getPlatformLabel = (platform) => {
        const p = [...PLATFORMS, ...EXTRA_CARD_PLATFORMS, ...MEDICAL_CARD_PLATFORMS,
                   ...MEDICAL_CARD_LINK_SLOTS].find(pl => pl.value === platform);
        return p ? p.label : platform;
    };
    // What a card can be programmed to: the six shared platforms and TikTok for
    // everyone, RateMDs only for a medical organization's card.
    const platformsForCard = (card) => (
        isMedicalCard(card)
            ? [...PLATFORMS, ...EXTRA_CARD_PLATFORMS, ...MEDICAL_CARD_PLATFORMS]
            : [...PLATFORMS, ...EXTRA_CARD_PLATFORMS]
    );
    // A card routed to a clinic link slot is not re-pointed from the fleet
    // list (the slot is set in the clinic's own routing), so it reads as a
    // label instead of an empty dropdown.
    const platformIsSelectable = (card) => platformsForCard(card).some(p => p.value === card?.platform);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
            </div>
        );
    }

    return (
        <TooltipProvider delayDuration={150}>
        <div className="space-y-6" data-testid="cards-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-foreground flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {t('cards.title')}
                        <HelpTooltip text={t('cards.titleHelp')} />
                    </h1>
                    <p className="text-muted-foreground mt-1">
                        {t('cards.subtitle')}
                    </p>
                </div>
                <div className="flex gap-2">
                    <Button onClick={() => loadData()} variant="outline" size="sm">
                        <RefreshCw className="w-4 h-4 mr-2" />
                        {t('common.refresh')}
                    </Button>
                    {!isPartner() && (
                    <TooltipProvider>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button onClick={() => setIsCreateModalOpen(true)} data-testid="add-card-btn" data-insight-id="cards.workflow.issue-new" data-insight-label="Issue new card">
                                    <Plus className="w-4 h-4 mr-2" />
                                    {t('cards.addCard')}
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p className="text-sm">{t('cards.addCardTooltip')}</p>
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                    )}
                    {/* v1.1 — Bulk Sequential Card Creation. Reuses POST /cards/bulk
                        which already loops + skips duplicates. */}
                    {!isPartner() && (
                    <TooltipProvider>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button
                                    onClick={() => setIsBulkCreateOpen(true)}
                                    variant="outline"
                                    data-testid="bulk-create-cards-btn"
                                    data-insight-id="tap-engine-bulk-create"
                                    data-insight-label="Bulk create Tap Engine cards"
                                >
                                    <Layers className="w-4 h-4 mr-2" />
                                    {t('bulkCreate.button', 'Bulk Create')}
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p className="text-sm">{t('bulkCreate.buttonTooltip', 'Generate a sequential range of cards in one operation.')}</p>
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                    )}
                </div>
            </div>

            {/* Filters & Search */}
            <Card>
                <CardContent className="pt-4">
                    <div className="flex flex-col lg:flex-row gap-4">
                        <div className="flex-1">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                <Input
                                    placeholder={t('cards.searchPlaceholder')}
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="pl-10"
                                    data-testid="search-cards"
                                />
                            </div>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {isSuperAdmin() && (
                                <Select value={filterOrganization} onValueChange={setFilterOrganization}>
                                    <SelectTrigger className="w-[180px]" data-testid="filter-organization">
                                        <Building2 className="w-4 h-4 mr-2" />
                                        <SelectValue placeholder={t('common.organization')} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">{t('cards.allOrganizations')}</SelectItem>
                                        {organizations.map(org => (
                                            <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            )}
                            <Select value={filterLocation} onValueChange={setFilterLocation}>
                                <SelectTrigger className="w-[160px]" data-testid="filter-location">
                                    <MapPin className="w-4 h-4 mr-2" />
                                    <SelectValue placeholder={t('common.location')} />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">{t('cards.allLocations')}</SelectItem>
                                    {locations.map(loc => (
                                        <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <Select value={filterStatus} onValueChange={setFilterStatus}>
                                <SelectTrigger className="w-[140px]" data-testid="filter-status">
                                    <Filter className="w-4 h-4 mr-2" />
                                    <SelectValue placeholder={t('common.status')} />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">{t('cards.allStatus')}</SelectItem>
                                    <SelectItem value="active">{t('common.active')}</SelectItem>
                                    <SelectItem value="unassigned">{t('cards.unassigned')}</SelectItem>
                                    <SelectItem value="inactive">{t('common.inactive')}</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Bulk Actions */}
            {selectedCards.length > 0 && (
                <Card className="bg-emerald-50 border-emerald-200">
                    <CardContent className="py-3">
                        <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-emerald-800">
                                {t('cards.selectedCards', { count: selectedCards.length })}
                            </span>
                            <div className="flex gap-2">
                                {canChangePlatform() && !isPartner() && (
                                    <Button 
                                        size="sm" 
                                        variant="outline"
                                        onClick={() => setIsBulkPlatformModalOpen(true)}
                                        data-testid="bulk-platform-btn"
                                        data-insight-id="cards.workflow.bulk-platform"
                                        data-insight-label="Bulk change platform"
                                    >
                                        <Globe className="w-4 h-4 mr-2" />
                                        {t('cards.changePlatform')}
                                    </Button>
                                )}
                                {isSuperAdmin() && (
                                    <Button 
                                        size="sm" 
                                        variant="outline"
                                        onClick={() => setIsBulkOrgModalOpen(true)}
                                        data-testid="bulk-org-btn"
                                        data-insight-id="cards.workflow.bulk-org-location"
                                        data-insight-label="Bulk reassign organization / location"
                                    >
                                        <Building2 className="w-4 h-4 mr-2" />
                                        {t('cards.reassignOrgLocation')}
                                    </Button>
                                )}
                                <Button 
                                    size="sm" 
                                    variant="ghost"
                                    onClick={() => setSelectedCards([])}
                                >
                                    {t('cards.clearSelection')}
                                </Button>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Cards Table */}
            <Card>
                <CardContent className="p-0">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead className="w-12">
                                    <Checkbox
                                        checked={selectedCards.length === filteredCards.length && filteredCards.length > 0}
                                        onCheckedChange={selectAllCards}
                                        data-testid="select-all-cards"
                                    />
                                </TableHead>
                                <TableHead>{t('cards.cardId')}</TableHead>
                                <TableHead>{t('common.name')}</TableHead>
                                {isSuperAdmin() && <TableHead>{t('common.organization')}</TableHead>}
                                <TableHead>{t('common.location')}</TableHead>
                                <TableHead>{t('common.employee')}</TableHead>
                                <TableHead>{t('cards.platform')}</TableHead>
                                <TableHead>{t('cards.taps')}</TableHead>
                                <TableHead>{t('common.status')}</TableHead>
                                <TableHead className="w-28">{t('cards.qrCode')}</TableHead>
                                <TableHead className="w-12"></TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {filteredCards.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={isSuperAdmin() ? 10 : 9} className="text-center py-12">
                                        <div className="flex flex-col items-center">
                                            <CreditCard className="w-10 h-10 text-muted-foreground/40 mb-3" />
                                            <p className="font-medium text-foreground mb-1">
                                                {cards.length === 0 ? t('empty.cardsEmptyTitle', 'No Tap Engine Cards yet') : t('empty.cardsTitle', 'No cards found')}
                                            </p>
                                            <p className="text-sm text-muted-foreground max-w-sm">
                                                {cards.length === 0 ? t('empty.cardsEmptyDesc', 'Create your first card to get started.') : t('empty.cardsDesc', 'There are no cards matching your current filters.')}
                                            </p>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filteredCards.map((card, idx) => (
                                    <TableRow
                                        key={card.id}
                                        data-testid={`card-row-${card.card_id}`}
                                        data-insight-id={idx === 0 ? "cards.workflow.row" : undefined}
                                        data-insight-label={idx === 0 ? "Card inventory row" : undefined}
                                    >
                                        <TableCell>
                                            <Checkbox
                                                checked={selectedCards.includes(card.id)}
                                                onCheckedChange={() => toggleCardSelection(card.id)}
                                            />
                                        </TableCell>
                                        <TableCell className="font-medium">
                                            <div className="flex flex-col gap-0.5">
                                                <div className="flex items-center gap-2">
                                                    <CreditCard className="w-4 h-4 text-emerald-600" />
                                                    {card.card_id}
                                                </div>
                                                {card.timing_enabled && (
                                                    <Tooltip>
                                                        <TooltipTrigger asChild>
                                                            <span
                                                                className="text-[10px] text-slate-500 dark:text-slate-400 pl-6 leading-tight cursor-help underline decoration-dotted decoration-slate-300 underline-offset-2"
                                                                data-testid={`current-routing-${card.card_id}`}
                                                                tabIndex={0}
                                                            >
                                                                {t('timing.currentlyRouting', 'Currently routing to')}:{' '}
                                                                <span className="font-medium">
                                                                    {card.current_routing_source === 'block'
                                                                        ? formatPlatformLabel(card.current_routing_platform)
                                                                        : t('timing.routingDefault', 'Default')}
                                                                </span>
                                                            </span>
                                                        </TooltipTrigger>
                                                        <TooltipContent side="bottom" className="max-w-[320px] whitespace-pre-line text-[11px] leading-relaxed">
                                                            <p className="font-semibold mb-1">{t('timing.dailySchedule', 'Daily schedule')}</p>
                                                            {(card.timing_schedule || []).length > 0
                                                                ? formatScheduleSummary(card.timing_schedule)
                                                                : t('timing.noBlocks', 'No time blocks — using default platform.')}
                                                        </TooltipContent>
                                                    </Tooltip>
                                                )}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <span className="text-sm text-muted-foreground">
                                                {card.name || <span className="text-muted-foreground italic">{t('cards.noName')}</span>}
                                            </span>
                                        </TableCell>
                                        {isSuperAdmin() && (
                                            <TableCell>
                                                <div className="flex items-center gap-1">
                                                    <Building2 className="w-3 h-3 text-muted-foreground" />
                                                    <span className="text-sm">{card.organization_name || 'N/A'}</span>
                                                </div>
                                            </TableCell>
                                        )}
                                        <TableCell>
                                            <div className="flex items-center gap-1">
                                                <MapPin className="w-3 h-3 text-muted-foreground" />
                                                <span className="text-sm">{card.location_name || 'N/A'}</span>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            {card.employee_name ? (
                                                <div className="flex items-center gap-1">
                                                    <User className="w-3 h-3 text-muted-foreground" />
                                                    <span className="text-sm">{card.employee_name}</span>
                                                </div>
                                            ) : (
                                                <span className="text-muted-foreground text-sm">{t('cards.unassigned')}</span>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {canChangePlatform() && platformIsSelectable(card) ? (
                                                <Select 
                                                    value={card.platform} 
                                                    onValueChange={(val) => handlePlatformChange(card.id, val)}
                                                >
                                                    <SelectTrigger className="w-[140px] h-8 text-xs">
                                                        <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {platformsForCard(card).map(p => (
                                                            <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            ) : (
                                                <Badge variant="outline" className="text-xs">
                                                    {getPlatformLabel(card.platform)}
                                                </Badge>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Badge variant="outline" className="font-mono">
                                                {card.tap_count || 0}
                                            </Badge>
                                        </TableCell>
                                        <TableCell>
                                            {getStatusBadge(card.status)}
                                        </TableCell>
                                        <TableCell>
                                            {card.card_id && (
                                                <div className="flex items-center gap-0.5">
                                                    <button
                                                        onClick={() => { setQrModalIsAutomotive(isAutomotiveCard(card)); setQrModalCardId(card.card_id); }}
                                                        className="p-1 rounded hover:bg-muted transition-colors cursor-pointer"
                                                        title={t('cards.qrCode')}
                                                        data-testid={`qr-preview-${card.card_id}`}
                                                    >
                                                        <QRCodeSVG
                                                            value={isAutomotiveCard(card) ? `${window.location.origin}/vehicle/${card.card_id}` : `${window.location.origin}/api/tap?card=${card.card_id}`}
                                                            size={28}
                                                            level="L"
                                                        />
                                                    </button>
                                                    <Button
                                                        variant="ghost" size="icon" className="h-7 w-7"
                                                        title={t('cards.copyUrl')}
                                                        data-testid={`row-copy-url-${card.card_id}`}
                                                        onClick={() => {
                                                            const url = `${window.location.origin}/api/tap?card=${card.card_id}`;
                                                            const fallbackCopy = () => {
                                                                const ta = document.createElement('textarea');
                                                                ta.value = url;
                                                                document.body.appendChild(ta);
                                                                ta.select();
                                                                document.execCommand('copy');
                                                                document.body.removeChild(ta);
                                                                toast.success(t('cards.urlCopied'));
                                                            };
                                                            if (navigator.clipboard?.writeText) {
                                                                navigator.clipboard.writeText(url)
                                                                    .then(() => toast.success(t('cards.urlCopied')))
                                                                    .catch(fallbackCopy);
                                                            } else { fallbackCopy(); }
                                                        }}
                                                    >
                                                        <Copy className="w-3.5 h-3.5" />
                                                    </Button>
                                                    <Button
                                                        variant="ghost" size="icon" className="h-7 w-7"
                                                        title={t('cards.downloadPng')}
                                                        data-testid={`row-qr-download-${card.card_id}`}
                                                        onClick={() => {
                                                            const link = document.createElement('a');
                                                            link.href = `${window.location.origin}/api/cards/qr/${card.card_id}?format=png&size=1024`;
                                                            link.download = `qr-${card.card_id}.png`;
                                                            link.click();
                                                        }}
                                                    >
                                                        <Download className="w-3.5 h-3.5" />
                                                    </Button>
                                                </div>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <DropdownMenu>
                                                <DropdownMenuTrigger asChild>
                                                    <Button variant="ghost" size="icon" className="h-8 w-8">
                                                        <MoreVertical className="w-4 h-4" />
                                                    </Button>
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent align="end">
                                                    {!isPartner() && (
                                                    <DropdownMenuItem onClick={() => openEditNameModal(card)} data-insight-id={idx === 0 ? "cards.workflow.edit-name" : undefined} data-insight-label={idx === 0 ? "Edit card name" : undefined}>
                                                        <Edit3 className="w-4 h-4 mr-2" />
                                                        {t('cards.editName')}
                                                    </DropdownMenuItem>
                                                    )}
                                                    {!isPartner() && (
                                                    <DropdownMenuItem onClick={() => openAssignModal(card)} data-insight-id={idx === 0 ? "cards.workflow.reassign-employee" : undefined} data-insight-label={idx === 0 ? "Assign or reassign employee" : undefined}>
                                                        <UserPlus className="w-4 h-4 mr-2" />
                                                        {card.employee_id ? t('cards.reassignEmployee') : t('cards.assignEmployee')}
                                                    </DropdownMenuItem>
                                                    )}
                                                    {canChangeCardLocation() && !isPartner() && !isLocationManager() && (
                                                        <DropdownMenuItem onClick={() => openEditLocationModal(card)} data-insight-id={idx === 0 ? "cards.workflow.change-location" : undefined} data-insight-label={idx === 0 ? "Change card location" : undefined}>
                                                            <MapPin className="w-4 h-4 mr-2" />
                                                            {t('cards.changeLocation')}
                                                        </DropdownMenuItem>
                                                    )}
                                                    {isSuperAdmin() && (
                                                        <DropdownMenuItem onClick={() => openEditOrgModal(card)} data-insight-id={idx === 0 ? "cards.workflow.change-organization" : undefined} data-insight-label={idx === 0 ? "Change card organization" : undefined}>
                                                            <Building2 className="w-4 h-4 mr-2" />
                                                            {t('cards.changeOrganization')}
                                                        </DropdownMenuItem>
                                                    )}
                                                    <DropdownMenuSeparator />
                                                    {(isSuperAdmin() || (isClientAdmin() && (canUseAdsFeature() || hasPermission('review_shield_toggle') || card.interaction_mode === 'review_shield' || card.global_review_enabled))) && (
                                                        <DropdownMenuItem onClick={() => openLandingSettingsModal(card)} data-insight-id={idx === 0 ? "cards.workflow.card-settings" : undefined} data-insight-label={idx === 0 ? "Open Card Settings" : undefined}>
                                                            <Smartphone className="w-4 h-4 mr-2" />
                                                            Card Settings
                                                            {card.landing_page_enabled && (
                                                                <Badge className="ml-2 bg-emerald-100 text-emerald-700 text-xs">AD</Badge>
                                                            )}
                                                            {card.interaction_mode === 'review_shield' && (
                                                                <Badge className="ml-2 bg-blue-100 text-blue-700 text-xs">SHIELD</Badge>
                                                            )}
                                                        </DropdownMenuItem>
                                                    )}
                                                    <DropdownMenuItem onClick={() => {
                                                        const base = window.location.origin;
                                                        const auto = isAutomotiveCard(card);
                                                        const url = auto
                                                            ? `${base}/vehicle/${card.card_id}`
                                                            : `${base}/api/tap?card=${card.card_id}`;
                                                        setCardUrlInfo({ cardId: card.card_id, url, isAutomotive: auto });
                                                    }} data-testid={`card-url-${card.card_id}`}>
                                                        <Link2 className="w-4 h-4 mr-2" />
                                                        {t('cards.cardUrl')}
                                                    </DropdownMenuItem>
                                                    <DropdownMenuItem
                                                        onClick={() => { setQrModalIsAutomotive(isAutomotiveCard(card)); setQrModalCardId(card.card_id); }}
                                                        data-testid={`qr-menu-${card.card_id}`}
                                                    >
                                                        <QrCode className="w-4 h-4 mr-2" />
                                                        {t('cards.qrCode')}
                                                    </DropdownMenuItem>
                                                    {!isPartner() && <DropdownMenuSeparator />}
                                                    {!isPartner() && (
                                                    <DropdownMenuItem 
                                                        onClick={() => handleDelete(card.id)}
                                                        className="text-red-600"
                                                        data-testid={`card-delete-${card.card_id}`}
                                                        data-insight-id={idx === 0 ? "cards.workflow.delete" : undefined}
                                                        data-insight-label={idx === 0 ? "Delete card" : undefined}
                                                    >
                                                        <Trash2 className="w-4 h-4 mr-2" />
                                                        {t('cards.deleteCard')}
                                                    </DropdownMenuItem>
                                                    )}
                                                </DropdownMenuContent>
                                            </DropdownMenu>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>

            {/* Stats Summary */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                    <CardContent className="pt-4">
                        <div className="text-2xl font-bold text-foreground">{cards.length}</div>
                        <div className="text-sm text-muted-foreground">{t('dashboard.totalCards')}</div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="pt-4">
                        <div className="text-2xl font-bold text-emerald-600">
                            {cards.filter(c => c.status === 'active').length}
                        </div>
                        <div className="text-sm text-muted-foreground">{t('common.active')}</div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="pt-4">
                        <div className="text-2xl font-bold text-amber-600">
                            {cards.filter(c => c.status === 'unassigned').length}
                        </div>
                        <div className="text-sm text-muted-foreground">{t('cards.unassigned')}</div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="pt-4">
                        <div className="text-2xl font-bold text-muted-foreground">
                            {cards.reduce((sum, c) => sum + (c.tap_count || 0), 0)}
                        </div>
                        <div className="text-sm text-muted-foreground">{t('dashboard.totalTaps')}</div>
                    </CardContent>
                </Card>
            </div>

            {/* Create Cards Modal */}
            <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                            {t('cards.addNewCards')}
                        </DialogTitle>
                        <DialogDescription>
                            {t('cards.addNewCardsDesc')}
                        </DialogDescription>
                    </DialogHeader>
                    
                    <Tabs value={createTab} onValueChange={setCreateTab}>
                        <TabsList className="grid w-full grid-cols-2">
                            <TabsTrigger value="manual">{t('cards.manualEntry')}</TabsTrigger>
                            <TabsTrigger value="csv">{t('cards.csvImport')}</TabsTrigger>
                        </TabsList>
                        
                        <TabsContent value="manual" className="space-y-4">
                            <div>
                                <Label className="mb-2 block">{t('cards.cardIdsLabel')}</Label>
                                <textarea
                                    value={newCardIds}
                                    onChange={(e) => setNewCardIds(e.target.value)}
                                    placeholder="DEL001&#10;DEL002&#10;DEL003"
                                    className="w-full h-32 px-3 py-2 border border-slate-200 rounded-md text-sm font-mono"
                                    data-testid="new-card-ids"
                                />
                            </div>
                        </TabsContent>
                        
                        <TabsContent value="csv" className="space-y-4">
                            <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center">
                                <FileSpreadsheet className="w-10 h-10 mx-auto text-muted-foreground mb-2" />
                                <p className="text-sm text-muted-foreground mb-3">
                                    {t('cards.csvUploadDesc')}
                                </p>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept=".csv"
                                    onChange={handleCSVUpload}
                                    className="hidden"
                                />
                                <Button variant="outline" onClick={() => fileInputRef.current?.click()}>
                                    <Upload className="w-4 h-4 mr-2" />
                                    {t('cards.chooseCSVFile')}
                                </Button>
                            </div>
                            {newCardIds && (
                                <div>
                                    <Label className="mb-2 block">{t('cards.loadedCardIds', { count: newCardIds.split('\n').filter(Boolean).length })}</Label>
                                    <textarea
                                        value={newCardIds}
                                        onChange={(e) => setNewCardIds(e.target.value)}
                                        className="w-full h-24 px-3 py-2 border border-slate-200 rounded-md text-sm font-mono"
                                    />
                                </div>
                            )}
                        </TabsContent>
                    </Tabs>

                    <div className="space-y-4 pt-4 border-t">
                        {isSuperAdmin() && (
                            <div>
                                <Label className="mb-2 block">{t('common.organization')} *</Label>
                                <Select value={newCardOrganization} onValueChange={(val) => {
                                    setNewCardOrganization(val);
                                    setNewCardLocation(''); // Reset location when org changes
                                }}>
                                    <SelectTrigger data-testid="new-card-organization">
                                        <SelectValue placeholder={t('cards.selectOrganization')} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {organizations.map(org => (
                                            <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}
                        
                        <div>
                            <Label className="mb-2 block">{t('common.location')} *</Label>
                            <Select value={newCardLocation} onValueChange={setNewCardLocation}>
                                <SelectTrigger data-testid="new-card-location">
                                    <SelectValue placeholder={t('cards.selectLocation')} />
                                </SelectTrigger>
                                <SelectContent>
                                    {(isSuperAdmin() && newCardOrganization
                                        ? getLocationsForOrg(newCardOrganization)
                                        : locations
                                    ).map(loc => (
                                        <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div>
                            <Label className="mb-2 block">{t('cards.platform')} ({t('common.optional')})</Label>
                            <Select value={newCardPlatform || '__default__'} onValueChange={(val) => setNewCardPlatform(val === '__default__' ? '' : val)}>
                                <SelectTrigger data-testid="new-card-platform">
                                    <SelectValue placeholder={t('cards.useOrgDefault')} />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="__default__">{t('cards.useOrgDefault')}</SelectItem>
                                    {PLATFORMS.map(p => (
                                        <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsCreateModalOpen(false)}>
                            {t('common.cancel')}
                        </Button>
                        <Button onClick={handleCreateCards} data-testid="create-cards-btn">
                            {t('cards.createCards')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Assign Employee Modal */}
            <Dialog open={isAssignModalOpen} onOpenChange={setIsAssignModalOpen}>
                <DialogContent data-insight-id="cards.workflow.assign-employee" data-insight-label="Assign card to employee">
                    <DialogHeader>
                        <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                            {selectedCard?.employee_id ? 'Reassign Card' : 'Assign Card'} - {selectedCard?.card_id}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="py-4">
                        <Label className="mb-2 block">Select Employee</Label>
                        <Select value={selectedEmployee || '__unassign__'} onValueChange={(val) => setSelectedEmployee(val === '__unassign__' ? '' : val)}>
                            <SelectTrigger data-testid="select-employee">
                                <SelectValue placeholder="Choose an employee..." />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="__unassign__">Unassign (No employee)</SelectItem>
                                {(selectedCard?.organization_id 
                                    ? getEmployeesForOrg(selectedCard.organization_id)
                                    : employees
                                ).filter(emp => emp.id).map(emp => (
                                    <SelectItem key={emp.id} value={emp.id}>
                                        {emp.name} {emp.email ? `(${emp.email})` : ''}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsAssignModalOpen(false)}>
                            Cancel
                        </Button>
                        <Button onClick={handleAssign} data-testid="confirm-assign-btn">
                            {selectedEmployee ? 'Assign' : 'Unassign'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Edit Organization/Location Modal */}
            <Dialog open={isEditOrgModalOpen} onOpenChange={setIsEditOrgModalOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                            Change Organization/Location - {selectedCard?.card_id}
                        </DialogTitle>
                        <DialogDescription>
                            Reassign this card to a different organization and location. 
                            The employee assignment will be cleared.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-4 space-y-4">
                        <div>
                            <Label className="mb-2 block">Organization</Label>
                            <Select value={editOrganization} onValueChange={(val) => {
                                setEditOrganization(val);
                                setEditLocation(''); // Reset location when org changes
                            }}>
                                <SelectTrigger data-testid="edit-organization">
                                    <SelectValue placeholder="Select organization..." />
                                </SelectTrigger>
                                <SelectContent>
                                    {organizations.map(org => (
                                        <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="mb-2 block">Location</Label>
                            <Select value={editLocation} onValueChange={setEditLocation} disabled={!editOrganization}>
                                <SelectTrigger data-testid="edit-location">
                                    <SelectValue placeholder="Select location..." />
                                </SelectTrigger>
                                <SelectContent>
                                    {getLocationsForOrg(editOrganization).map(loc => (
                                        <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsEditOrgModalOpen(false)}>
                            Cancel
                        </Button>
                        <Button onClick={handleEditOrganization} data-testid="confirm-edit-org-btn">
                            Save Changes
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Bulk Platform Change Modal */}
            <Dialog open={isBulkPlatformModalOpen} onOpenChange={setIsBulkPlatformModalOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                            Change Platform for {selectedCards.length} Cards
                        </DialogTitle>
                    </DialogHeader>
                    <div className="py-4">
                        <Label className="mb-2 block">Select Platform</Label>
                        <Select value={bulkPlatform} onValueChange={setBulkPlatform}>
                            <SelectTrigger data-testid="bulk-select-platform">
                                <SelectValue placeholder="Choose a platform..." />
                            </SelectTrigger>
                            <SelectContent>
                                {PLATFORMS.map(p => (
                                    <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsBulkPlatformModalOpen(false)}>
                            Cancel
                        </Button>
                        <Button onClick={handleBulkPlatformChange} data-testid="confirm-bulk-platform-btn">
                            Update {selectedCards.length} Cards
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Bulk Organization Change Modal */}
            <Dialog open={isBulkOrgModalOpen} onOpenChange={setIsBulkOrgModalOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                            Reassign {selectedCards.length} Cards
                        </DialogTitle>
                        <DialogDescription>
                            Move selected cards to a different organization and location.
                            All employee assignments will be cleared.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-4 space-y-4">
                        <div>
                            <Label className="mb-2 block">Organization</Label>
                            <Select value={bulkOrganization} onValueChange={(val) => {
                                setBulkOrganization(val);
                                setBulkLocation('');
                            }}>
                                <SelectTrigger data-testid="bulk-select-organization">
                                    <SelectValue placeholder="Select organization..." />
                                </SelectTrigger>
                                <SelectContent>
                                    {organizations.map(org => (
                                        <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="mb-2 block">Location</Label>
                            <Select value={bulkLocation} onValueChange={setBulkLocation} disabled={!bulkOrganization}>
                                <SelectTrigger data-testid="bulk-select-location">
                                    <SelectValue placeholder="Select location..." />
                                </SelectTrigger>
                                <SelectContent>
                                    {getLocationsForOrg(bulkOrganization).map(loc => (
                                        <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsBulkOrgModalOpen(false)}>
                            Cancel
                        </Button>
                        <Button onClick={handleBulkOrgChange} data-testid="confirm-bulk-org-btn">
                            Reassign {selectedCards.length} Cards
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Edit Card Name Modal */}
            <Dialog open={isEditNameModalOpen} onOpenChange={setIsEditNameModalOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                            Edit Card Name - {selectedCard?.card_id}
                        </DialogTitle>
                        <DialogDescription>
                            Give this card a custom name or label (e.g., &quot;Server Station 1&quot;, &quot;John&apos;s Card&quot;)
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-4">
                        <Label className="mb-2 block">Card Name</Label>
                        <Input
                            value={editCardName}
                            onChange={(e) => setEditCardName(e.target.value)}
                            placeholder="Enter card name..."
                            data-testid="edit-card-name-input"
                        />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsEditNameModalOpen(false)}>
                            Cancel
                        </Button>
                        <Button onClick={handleEditName} data-testid="confirm-edit-name-btn">
                            Save Name
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Edit Card Location Modal */}
            <Dialog open={isEditLocationModalOpen} onOpenChange={setIsEditLocationModalOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                            Change Location - {selectedCard?.card_id}
                        </DialogTitle>
                        <DialogDescription>
                            Reassign this card to a different location within your organization.
                            The employee assignment will be cleared.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-4">
                        <Label className="mb-2 block">Select Location</Label>
                        <Select value={editLocation} onValueChange={setEditLocation}>
                            <SelectTrigger data-testid="edit-card-location-select">
                                <SelectValue placeholder="Select location..." />
                            </SelectTrigger>
                            <SelectContent>
                                {(selectedCard && !isSuperAdmin() 
                                    ? locations.filter(l => l.organization_id === selectedCard.organization_id)
                                    : locations.filter(l => l.organization_id === selectedCard?.organization_id)
                                ).map(loc => (
                                    <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsEditLocationModalOpen(false)}>
                            Cancel
                        </Button>
                        <Button onClick={handleEditLocation} data-testid="confirm-edit-location-btn">
                            Update Location
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Landing Page Settings Modal */}
            <CardSettingsModal
                open={isLandingSettingsModalOpen}
                onOpenChange={requestCloseCardSettings}
                isDirty={isCardSettingsDirty}
                isSaving={isSavingCardSettings}
                changesDiff={cardSettingsDiff}
                isAutomotiveOrg={isAutomotiveCard(selectedCard)}
                isMedicalOrg={isMedicalCard(selectedCard)}
                selectedCard={selectedCard}
                state={{
                    interactionMode, landingPageEnabled,
                    landingShowText, landingHeadline, landingDescription,
                    landingLogoUrl, landingLogoSize, landingAutoRedirect, landingRedirectDelay,
                    countdownClockEnabled, countdownClockColor,
                    showYulontapBranding, multipleAdsEnabled, adImages, sameTextForAll, adTexts,
                    scheduleEnabled, scheduleTimezone, scheduleEntries, uploadingLogo,
                    reviewShieldLogoUrl, reviewShieldLogoSize, reviewShieldBackgroundImageUrl, languageMode,
                    cardStyle,
                    enabledLanguages,
                    leadGenerationOverride,
                    giftGeneratorSetupId,
                    setGiftGeneratorSetupId,
                    reviewShieldQuestions,
                    publicReviewThreshold,
                    privateReviewEnabled,
                    experienceType,
                    savingExperienceType,
                    guestExperienceVersion,
                    timingEnabled,
                    timingSchedule,
                    notificationEmails,
                    notificationPhones,
                    directResponseEnabled,
                    requireEmployeePin,
                    reviewCounterEnabled,
                    tapProtectionEnabled,
                    testMode,
                    duplicateProtectionEnabled,
                    duplicateWindowSeconds,
                    dwellProtectionEnabled,
                    dwellThresholdSeconds,
                    giftCardEnabled,
                    loyalReviewEnabled,
                    loyalReviewVoucherId,
                    loyalReviewAutoFlow,
                    giftGeneratorEnabled,
                    globalReviewEnabled, useReviewShieldWithGlobalReview, globalReviewDestinations,
                    globalReviewLogoUrl, globalReviewLogoSize, globalReviewBackgroundImageUrl, globalReviewMessageEn, globalReviewMessageFr,
                    hotelOpsLogoUrl, hotelOpsLogoSize, hotelOpsBackgroundImageUrl,
                    guestServicesContext, poolDrinksEnabled, poolFoodEnabled, poolTowelsEnabled,
                    poolOtherEnabled, poolDrinkMenuImageUrl, poolFoodMenuImageUrl,

                    landingVideoEnabled, mediaType, mediaVideoUrl, mediaVideoId, mediaVideoPosterUrl,
                    mediaVideoFallbackImage, mediaAutoplay, mediaLoop, mediaMuted,
                    mediaSkipEnabled, mediaSkipDelay, uploadingVideo, videoUploadProgress
                }}
                handlers={{
                    setInteractionMode, setLandingPageEnabled,
                    setLandingShowText, setLandingHeadline, setLandingDescription,
                    setLandingLogoUrl, setLandingLogoSize, setLandingAutoRedirect, setLandingRedirectDelay,
                    setCountdownClockEnabled, setCountdownClockColor,
                    setShowYulontapBranding, setMultipleAdsEnabled, setAdImages, setSameTextForAll, setAdTexts,
                    setScheduleEnabled, setScheduleTimezone, setScheduleEntries,
                    setPendingMode, setShowModeSwitchConfirm, setShowBulkApplyModal,
                    handleSaveLandingSettings, handleLogoUpload, handleAdImageUpload,
                    clearLogo: () => setLandingLogoUrl(''),
                    setReviewShieldLogoUrl, setReviewShieldLogoSize, setReviewShieldBackgroundImageUrl, setLanguageMode,
                    setCardStyle,
                    setEnabledLanguages,
                    setLeadGenerationOverride,
                    clearReviewShieldLogo, handleReviewShieldLogoUpload,
                    setReviewShieldQuestions,
                    setPublicReviewThreshold,
                    setPrivateReviewEnabled,
                    setExperienceType,
                    onExperienceTypeChange: handleExperienceTypeChange,
                    setGuestExperienceVersion,
                    setTimingEnabled,
                    setTimingSchedule,
                    setNotificationEmails,
                    setNotificationPhones,
                    setDirectResponseEnabled,
                    setRequireEmployeePin,
                    setReviewCounterEnabled,
                    setTapProtectionEnabled,
                    setTestMode,
                    setDuplicateProtectionEnabled,
                    setDuplicateWindowSeconds,
                    setDwellProtectionEnabled,
                    setDwellThresholdSeconds,
                    setGiftCardEnabled,
                    setLoyalReviewEnabled,
                    setLoyalReviewVoucherId,
                    setLoyalReviewAutoFlow,
                    setGiftGeneratorEnabled,
                    setGlobalReviewEnabled, setUseReviewShieldWithGlobalReview, setGlobalReviewDestinations,
                    setGlobalReviewLogoUrl, setGlobalReviewLogoSize, setGlobalReviewBackgroundImageUrl, setGlobalReviewMessageEn, setGlobalReviewMessageFr,

                    handleGlobalReviewLogoUpload, globalReviewLogoInputRef,
                    handleReviewShieldWallpaperUpload, handleGlobalReviewWallpaperUpload,
                    setHotelOpsLogoUrl, setHotelOpsLogoSize, setHotelOpsBackgroundImageUrl,
                    clearHotelOpsLogo, handleHotelOpsLogoUpload, handleHotelOpsWallpaperUpload,
                    setGuestServicesContext, setPoolDrinksEnabled, setPoolFoodEnabled,
                    setPoolTowelsEnabled, setPoolOtherEnabled, setPoolDrinkMenuImageUrl, setPoolFoodMenuImageUrl,
                    handlePoolDrinkMenuUpload, handlePoolFoodMenuUpload,
                    setLandingVideoEnabled, setMediaType, setMediaVideoUrl, setMediaVideoId,
                    setMediaVideoPosterUrl, setMediaVideoFallbackImage,
                    setMediaAutoplay, setMediaLoop, setMediaMuted, setMediaSkipEnabled, setMediaSkipDelay,
                    handleVideoUpload, removeVideo
                }}
                refs={{ logoInputRef, adImageInputRef, reviewShieldLogoInputRef, videoInputRef, reviewShieldWallpaperInputRef, globalReviewWallpaperInputRef, hotelOpsLogoInputRef, hotelOpsWallpaperInputRef, poolDrinkMenuInputRef, poolFoodMenuInputRef }}

                isSuperAdmin={isSuperAdmin}
                t={t}
                token={localStorage.getItem('yulontap_token')}
                canUseAds={canUseAdsFeature()}
                hasPermission={hasPermission}
            />

            {/* Card Settings Unsaved-Changes Exit Guard */}
            <Dialog open={cardSettingsExitGuard} onOpenChange={(o) => { if (!o) handleCardSettingsExitStay(); }}>
                <DialogContent data-testid="card-settings-unsaved-guard">
                    <DialogHeader>
                        <DialogTitle>{t('common.unsavedChanges')}</DialogTitle>
                        <DialogDescription>{t('common.unsavedChangesDesc')}</DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="flex-col sm:flex-row gap-2">
                        <Button variant="outline" onClick={handleCardSettingsExitDiscard} data-testid="card-settings-unsaved-discard">
                            {t('common.discard')}
                        </Button>
                        <Button variant="secondary" onClick={handleCardSettingsExitStay} data-testid="card-settings-unsaved-stay">
                            {t('common.stay')}
                        </Button>
                        <Button onClick={handleCardSettingsExitSave} data-testid="card-settings-unsaved-save">
                            {t('common.save')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Bulk Apply Modal */}
            <BulkApplyModal
                open={showBulkApplyModal}
                onOpenChange={setShowBulkApplyModal}
                bulkApplyOrg={bulkApplyOrg}
                setBulkApplyOrg={setBulkApplyOrg}
                bulkApplyLocation={bulkApplyLocation}
                setBulkApplyLocation={setBulkApplyLocation}
                bulkApplying={bulkApplying}
                handleBulkApply={handleBulkApply}
                organizations={organizations}
                locations={locations}
            />

            {/* Mode Switch Confirmation Dialog */}
            <ModeSwitchDialog
                open={showModeSwitchConfirm}
                onOpenChange={setShowModeSwitchConfirm}
                pendingMode={pendingMode}
                t={t}
                onConfirm={() => {
                    // v4.34.4 — Mirror handleModeChange's underlying-flag writes
                    // for the modes that needed a confirmation dialog (Review
                    // Shield ↔ Advertising). Other transitions are silent so
                    // they don't reach this branch.
                    if (pendingMode === 'review_shield') {
                        setInteractionMode('review_shield');
                        setLandingPageEnabled(false);
                    } else if (pendingMode === 'advertising') {
                        setInteractionMode('advertising');
                        setLandingPageEnabled(true);
                        setGlobalReviewEnabled(false);
                        setUseReviewShieldWithGlobalReview(false);
                    }
                    setShowModeSwitchConfirm(false);
                    setPendingMode(null);
                }}
                onCancel={() => { setShowModeSwitchConfirm(false); setPendingMode(null); }}
            />

            {/* Card URL Display Dialog — with inline QR code */}
            <Dialog open={!!cardUrlInfo} onOpenChange={(open) => { if (!open) setCardUrlInfo(null); }}>
                <DialogContent className="max-w-md" data-testid="card-url-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Link2 className="w-5 h-5" />
                            {t('cards.cardUrl')}
                        </DialogTitle>
                    </DialogHeader>
                    {cardUrlInfo && (
                        <div className="space-y-4">
                            <div>
                                <p className="text-xs text-muted-foreground mb-1">{t('cards.cardId')}</p>
                                <p className="font-mono text-sm font-semibold" data-testid="card-url-id">{cardUrlInfo.cardId}</p>
                            </div>
                            <div>
                                <p className="text-xs text-muted-foreground mb-1">
                                    {cardUrlInfo.isAutomotive
                                        ? t('cards.automotiveDestination', 'Automotive destination URL')
                                        : t('cards.tapUrl')}
                                </p>
                                <div className="flex items-center gap-2">
                                    <code className="flex-1 text-xs bg-slate-100 dark:bg-slate-800 px-3 py-2 rounded-lg border break-all select-all" data-testid="card-url-value">
                                        {cardUrlInfo.url}
                                    </code>
                                    <Button variant="outline" size="sm" onClick={() => {
                                        navigator.clipboard.writeText(cardUrlInfo.url).then(() => {
                                            toast.success(t('cards.urlCopied'));
                                        });
                                    }} data-testid="card-url-copy-btn">
                                        <Copy className="w-4 h-4" />
                                    </Button>
                                </div>
                                {cardUrlInfo.isAutomotive && (
                                    <p className="text-xs text-muted-foreground mt-1.5" data-testid="card-url-automotive-help">
                                        {t('cards.automotiveUrlHelp', 'Program the physical Tap card with this URL. It is permanent — it always resolves to the vehicle currently assigned to this card, even after the vehicle is sold and reassigned.')}
                                    </p>
                                )}
                            </div>
                            {/* Inline QR Code */}
                            <div className="flex flex-col items-center gap-2 pt-2 border-t">
                                <p className="text-xs text-muted-foreground">{t('cards.qrCode')}</p>
                                <button
                                    onClick={() => { setQrModalIsAutomotive(!!cardUrlInfo.isAutomotive); setCardUrlInfo(null); setQrModalCardId(cardUrlInfo.cardId); }}
                                    className="bg-white p-3 rounded-xl border hover:shadow-md transition-shadow cursor-pointer"
                                    data-testid="card-url-qr-preview"
                                >
                                    <QRCodeSVG value={cardUrlInfo.url} size={120} level="H" />
                                </button>
                                <p className="text-xs text-muted-foreground">{t('cards.qrCode')} — {t('cards.downloadPng')}, {t('cards.printQr')}</p>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* QR Code Modal */}
            <QrCodeModal
                open={!!qrModalCardId}
                onClose={() => { setQrModalCardId(null); setQrModalIsAutomotive(false); }}
                cardId={qrModalCardId}
                isAutomotive={qrModalIsAutomotive}
            />

            {/* v1.1 — Bulk Sequential Card Creation dialog */}
            <BulkCreateCardsDialog
                open={isBulkCreateOpen}
                onOpenChange={setIsBulkCreateOpen}
                organizationId={filterOrganization && filterOrganization !== 'all' ? filterOrganization : (organizations?.[0]?.id || '')}
                organizations={organizations}
                locations={locations}
                defaultLocationId={filterLocation && filterLocation !== 'all' ? filterLocation : ''}
                onCreated={() => loadData()}
            />
        </div>
        </TooltipProvider>
    );
};

export default Cards;
